import React, { useEffect, useRef, useState, useCallback } from "react";
import { NavLink, useNavigate } from "./Navigation.jsx";
import Footer from "./Footer.jsx";
import { useCurrencySymbol } from "./Currency.jsx";
import { wsRequest, onBroadcast, setDemoRouteToken, onSessionInvalid, triggerSessionInvalid } from "./wsClient.js";
import { onAuthChanged, announceAuthChange } from "./authPrompt.js";
import AuthForm from "./AuthForm.jsx";
import { showToast } from "./toast.js";
import { siteDateTime, siteTime } from "./siteTime.js";
import { setSiteTimeZone } from "./siteTime.js";
import { canStillUseFreePixel, freeGuessStillToCome } from "./freeEntryBanner.js";

// What is actually free, in words, from what the site has switched on.
//
// A partner may offer the free guess without the free pixel, or the other
// way round. Naming both regardless promises something that does not
// exist, and the buyer finds out by pressing a button that is not there.
function freeRoutesSentence(config) {
  const pixel = config?.freeEntryPixelAvailable;
  const guess = config?.freeEntryGuessAvailable;
  if (pixel && guess) return "uncover one free pixel and submit one free guess";
  if (pixel) return "uncover one free pixel";
  if (guess) return "submit one free guess";
  return "enter for free";
}


const LOCAL_STORAGE_KEY = "589comps_my_regions";
const USER_TOKEN_KEY = "589comps_user_token";
// Backend runs on port 4000, on the same host you're browsing from.
// Change this if you later put the backend behind its own domain/proxy.
const API_BASE = ""; // same-origin — nginx proxies /api and /ws to the backend

// WHAT A BUYER IS TOLD WHILE THE WEBHOOK IS AWAITED. Shown in two places
// — the banner on the live page, and the loading view for somebody who
// lands back before the page has config — which used to carry two
// spellings of the same sentence. 1.555.0 gave the widget the same
// message, and a test asserts the widget's copy and this one are the same
// words; two spellings here would have made that test pick one at random.
export const PENDING_PAYMENT_NOTICE = "Confirming your payment — this can take a few seconds…";

// ---- WHAT A BUYER IS TOLD ONCE THE PAYMENT HAS SETTLED (1.867.0) ----
//
// A pending purchase's `status` has SIX values - they are listed where
// pendingPurchases is declared in server.js. This page branched on TWO of
// them, which put the one sentence that makes a POSITIVE CLAIM ABOUT
// MONEY on the one status where that claim can be false:
//
//   "failed" DOES NOT MEAN NO MONEY MOVED. The status route re-reconciles
//   a failed record for exactly that reason and says so in its own words:
//   "A purchase can be marked failed while the payment is in fact still
//   in flight or already settled". And one site marks a purchase failed
//   AFTER the provider has said the payment SUCCEEDED, because the
//   buyer's account could not be found - a state that now raises an admin
//   issue, because somebody is owed money back. Telling that buyer
//   "nothing was charged" is a false statement about their own money.
//
//   "expired" DOES mean no money moved, and is the only status that does:
//   the provider said the payment job expired unpaid, and the backend logs
//   "Nothing was taken and nothing granted" as it writes it. So that is
//   where the reassurance belongs. It is also the only one with no
//   reference, because nothing happened and no issue is raised for it
//   either - an abandoned checkout is routine and needs no investigating.
//
//   "refunded" and "refund-failed" are settled too - the competition
//   ended mid-payment - and had no branch at all, so both fell through to
//   the sixty-second give-up message. A buyer whose refund had FAILED,
//   who is owed money and holds nothing, was told the site was "still
//   waiting on payment confirmation".
//
// NOTHING HERE NAMES WHAT WAS BOUGHT. A credit top-up settles through
// this same poll with the same `pendingPurchase` parameter, so "nothing
// was charged for those pixels" was shown to buyers who had bought
// CREDIT. That is the 6 Sep defect, fixed in the confirmed branch below
// and left standing in this one. Saying "charged" rather than "charged
// for <goods>" cannot make the mistake at all, which is why none of these
// mention pixels or credit.
//
// THE WIDGET SAYS THE SAME WORDS from its own copy of this table, and a
// test asserts the two match - the same guard PENDING_PAYMENT_NOTICE has,
// for the same reason: the widget is the consumer that gets fixed later.
//
// KEYS ARE QUOTED UNIFORMLY. "refund-failed" has to be; the other three
// are so that the set of statuses handled here is one regex away for the
// test that checks it against the backend's own vocabulary.
export const PURCHASE_SETTLED_MESSAGES = {
  "expired": {
    type: "info",
    // A TEMPLATE LITERAL WITH NOTHING IN IT, deliberately: all four
    // are backticked so the test that compares these words with the
    // widget's copy lifts them with one expression instead of four.
    text: () => `That payment was never completed, so nothing was charged.`,
  },
  "failed": {
    type: "error",
    text: (ref) =>
      `We could not complete that purchase. If your statement shows a charge for it, quote reference ${ref} and we will put it right.`,
  },
  "refunded": {
    type: "warning",
    text: (ref) =>
      `The competition ended while you were paying, so your payment was refunded in full and nothing was bought. Reference ${ref} — the details are in your email.`,
  },
  "refund-failed": {
    type: "error",
    text: (ref) =>
      `The competition ended while you were paying and the refund did not go through. Nothing was bought and you are owed your money back — please get in touch quoting reference ${ref}.`,
  },
};

// AND WHAT THEY ARE TOLD WHEN IT NEVER SETTLES AT ALL. Thirty attempts,
// two seconds apart, then this. It makes no claim about the money either
// way, because at that point nothing here knows one: the webhook has not
// arrived and the record is still pending. The reference is the whole
// point of the sentence - this page strips the id from the address bar on
// arrival, so without it the buyer has nothing to quote to anybody.
export const PURCHASE_UNSETTLED_MESSAGE = (ref) =>
  `Still no confirmation of that payment. If your statement shows a charge, quote reference ${ref} and we will put it right.`;

// Shows "1:00" style countdown once 60s or more remain, and drops to plain
// "59s" style once under a minute — e.g. 1:00 -> 0:59... wait, spec: 1:00 then 59s.
function formatCountdown(ms) {
  const totalSeconds = Math.max(0, Math.round(ms / 1000));
  if (totalSeconds >= 60) {
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${String(seconds).padStart(2, "0")}`;
  }
  return `${totalSeconds}s`;
}

// Longer-range countdown for "next competition starts in..." — this can be
// days away, unlike the private-view countdown above.
// "Ends in 4m 12s", ticking once a second.
//
// Its own component with its own interval, so a competition page does not
// re-render every second just to move a clock. The existing "next
// competition starts in" text updates on the five-second poll instead,
// which is fine for a wait measured in hours and would look broken for
// one measured in seconds.
//
// Renders nothing when there is no end. computeEffectiveEnd returns null
// with the lottery off — not a missing value, but a competition that
// genuinely runs until somebody wins it.
// THE SERVER'S CLOCK, NOT THE BROWSER'S.
//
// Every moment this page counts down to — a start, an end, a lottery
// conversion, a private-view window — is decided by the server. Counting
// against the browser's clock meant a machine a few seconds adrift showed
// a countdown that lagged or overran the thing it was counting to.
//
// Measured from `serverTime`, which every config response carries. The
// same fix as the admin's (1.242.0); this page was missed because its
// countdowns are its own.
let serverClockOffsetMs = 0;
function setServerClockOffset(serverTime) {
  if (!Number.isFinite(serverTime)) return;
  serverClockOffsetMs = serverTime - Date.now();
}
function serverNow() {
  return Date.now() + serverClockOffsetMs;
}

function EndsInCountdown({ endsAt }) {
  const [now, setNow] = useState(() => serverNow());
  useEffect(() => {
    if (!endsAt) return undefined;
    const id = setInterval(() => setNow(serverNow()), 1000);
    return () => clearInterval(id);
  }, [endsAt]);
  if (!endsAt) return null;
  const left = endsAt - now;
  if (left <= 0) return null;
  return (
    <p style={{ margin: "0 0 8px", fontSize: 15, fontWeight: 600 }}>
      Competition ends in <strong>{formatLongCountdown(left)}</strong>
    </p>
  );
}

function formatLongCountdown(ms) {
  const totalSeconds = Math.max(0, Math.round(ms / 1000));
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (days > 0) return `${days}d ${hours}h ${minutes}m`;
  if (hours > 0) return `${hours}h ${minutes}m ${seconds}s`;
  if (minutes > 0) return `${minutes}m ${seconds}s`;
  return `${seconds}s`;
}

// Shown on both the active and ended page states whenever simulation is
// running — targetTime is whichever event is next known (the active mock
// competition's own end time, or the next one's computed start time
// during the gap between competitions). Relies on the parent already
// re-rendering once a second (both pageMode branches share the same
// tick-driven interval), so this doesn't need its own timer. resolved/
// total reflect every mock competition that currently exists, not just
// ones from a single generate-competitions batch, so this stays accurate
// across multiple batches.
function SimulationBanner({ targetTime, resolvedCompetitions, totalCompetitions, isCountingToOwnEnd }) {
  // Once every mock competition has resolved, there's nothing left for the
  // simulation to do — show that plainly rather than a countdown, even if
  // targetTime happens to be non-null (a real, non-mock competition queued
  // after the mock ones would otherwise misleadingly look like part of the
  // simulation still running).
  const allResolved = totalCompetitions > 0 && resolvedCompetitions >= totalCompetitions;
  // The competition currently being simulated is the last one in the
  // batch, AND the countdown showing is to that competition's own end
  // (not to it starting) — still genuinely active/counting down on its
  // own, but there's no further "next event" in the simulation sequence
  // for that particular countdown to lead to. Waiting for the last
  // competition to actually start is a different case — that countdown
  // stays, since "it starting" genuinely is the next event there.
  const isLastCompetition =
    isCountingToOwnEnd && totalCompetitions > 0 && resolvedCompetitions + 1 >= totalCompetitions;
  if (!allResolved && targetTime == null) return null;
  return (
    <div
      style={{
        display: "inline-block",
        fontSize: 13,
        padding: "6px 12px",
        borderRadius: 6,
        marginBottom: 12,
        background: "var(--toast-info-bg)",
        color: "var(--toast-info-icon)",
        border: "1px solid var(--toast-info-border)",
      }}
    >
      {allResolved ? (
        <>
          🔬 Simulation complete —{" "}
          <strong>
            {resolvedCompetitions} of {totalCompetitions}
          </strong>{" "}
          competitions simulated
        </>
      ) : isLastCompetition ? (
        <>
          🔬 Simulation running — Simulating{" "}
          <strong>
            {totalCompetitions} of {totalCompetitions}
          </strong>{" "}
          competitions
        </>
      ) : (
        <>
          🔬 Simulation running — next event in <strong>{formatLongCountdown(targetTime - serverNow())}</strong>
          {totalCompetitions > 0 && (
            <>
              {" "}
              · Simulating{" "}
              <strong>
                {Math.min(resolvedCompetitions + 1, totalCompetitions)} of {totalCompetitions}
              </strong>{" "}
              competitions
            </>
          )}
        </>
      )}
    </div>
  );
}

// Guess submission. Requires a logged-in user — the backend won't accept a
// guess otherwise. No flat free entry — bonus guesses earned by spending
// on pixel purchases are free, everything else is a paid entry.
function GuessForm({ token, answerMode, creditsPerEntry, creditsPerMajorUnitOnPixels, isLottery, onCorrect, onEntryUsed, connectionStale = false }) {
  const symbol = useCurrencySymbol();
  const [guess, setGuess] = useState("");
  const [pendingQuote, setPendingQuote] = useState(null); // { guess, isFree, creditsPerEntry }
  const [busy, setBusy] = useState(false);

  const requestQuote = async (e) => {
    e.preventDefault();
    if (!token) {
      // Don't even check guess status — there's nothing useful it could
      // tell an anonymous visitor, since submitting requires an account
      // either way. Reuses the same confirm-dialog UI as a real quote,
      // just showing a login prompt instead of price/confirm.
      setPendingQuote({ guess, needsLogin: true });
      return;
    }
    setBusy(true);
    try {
      const data = await wsRequest("guess.status", {}, token);
      setPendingQuote({
        guess,
        isFree: data.isFree,
        freeEntry: data.freeEntry,
        freeCredits: data.freeCredits,
        creditsPerEntry: data.creditsPerEntry,
        freeRemaining: Math.max(0, data.freeLimit - data.entriesUsed),
        baseFreeGuesses: data.baseFreeGuesses,
        creditsEarnedHere: data.creditsEarnedHere,
        // Entry credit, so the dialog can say the entry comes OUT OF A
        // BALANCE ALREADY PAID FOR rather than appearing to ask for money
        // again. ALWAYS PRESENT since 1.261.0 — they were omitted while
        // the setting was off, which left this dialog unable to say what
        // an entry cost on a site where it still cost that.
        creditsEnabled: data.creditsEnabled,
        creditBalance: data.creditBalance,
        // Whether buying more is even possible, which decides what the
        // short-balance branch below tells them to do.
        canTopUp: data.canTopUp,
      });
    } catch (err) {
      showToast(err.message || "Could not reach backend.", "error");
    }
    setBusy(false);
  };

  const confirmGuess = async (tokenOverride) => {
    setBusy(true);
    try {
      const data = await wsRequest("guess.submit", { guess: pendingQuote.guess }, tokenOverride || token);
      setPendingQuote(null);
      setGuess("");
      if (data.correct) {
        onCorrect();
      } else {
        // EVERY GUESS COSTS CREDIT NOW, so there is no allowance left to
        // count against. This computed `freeLimit - entriesUsed`, which
        // is always 0 since 17 Sep 2026 — the condition was always true
        // and the arithmetic said nothing.
        //
        // The sentence itself was right and stays: after a wrong guess,
        // the next one costs what an entry costs. Which pot it comes out
        // of is the confirm step's business, not this toast's.
        const extra =
      creditsPerEntry > 0
        ? ` Your next guess will cost ${creditsPerEntry} credit${creditsPerEntry === 1 ? "" : "s"}.`
        : "";
        showToast(`Not quite — try again.${extra}`, "error");
        // REFRESH AFTER A WRONG GUESS TOO.
        //
        // onCorrect reloads the config when somebody WINS, and the purchase
        // and free-pixel routes reload after themselves — but a wrong guess
        // reloaded nothing. So the free-entry banner kept saying a free
        // guess was available after it had been used, until something else
        // happened to refresh the page.
        //
        // That is what made it look as though the free route charged
        // credits: the banner said free, the guess was paid, and the two
        // were describing different moments. Reported 6 Sep.
        if (onEntryUsed) onEntryUsed();
      }
    } catch (err) {
      showToast(err.message || "Could not reach backend.", "error");
    }
    setBusy(false);
  };

  const inputStyle = {
    padding: 8,
    marginRight: 8,
    background: "var(--bg-input)",
    border: "1px solid var(--border-color-light)",
    borderRadius: 6,
    color: "var(--text-primary)",
  };

  if (isLottery) {
    return (
      <div style={{ marginTop: 20, padding: 14, border: "1px solid var(--accent-yellow)", borderRadius: 8, color: "var(--accent-yellow)" }}>
        Entries are frozen — this competition has moved to a lottery draw. A winner will be picked randomly from
        purchased pixels at the finish time. You can still buy more of the image for better odds.
      </div>
    );
  }

  return (
    <div style={{ marginTop: 20, padding: 14, border: "1px solid var(--border-color)", borderRadius: 8 }}>
      <form onSubmit={requestQuote} style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        <input
          value={guess}
          onChange={(e) => setGuess(e.target.value)}
          placeholder="Guess what the hidden image is…"
          style={{ ...inputStyle, flex: 1, minWidth: 200 }}
        />
        <button type="submit" disabled={busy || !guess.trim()}>
          {busy ? "Checking…" : "Submit guess"}
        </button>
      </form>
      <div style={{ fontSize: 12, opacity: 0.6, marginTop: 6 }}>
        {answerMode === "exact" ? "Exact match required" : "Fuzzy match accepted"}
        {creditsPerEntry > 0 && ` · ${creditsPerEntry} credit${creditsPerEntry === 1 ? "" : "s"} per entry`}
        {creditsPerMajorUnitOnPixels > 0 &&
              ` · earn ${creditsPerMajorUnitOnPixels} credits for every ${symbol}1 you spend uncovering the image`}
      </div>

      {pendingQuote && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.6)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div style={{ background: "var(--bg-panel-alt)", padding: 24, borderRadius: 10, minWidth: 280, color: "var(--text-primary)" }}>
            {pendingQuote.needsLogin ? (
              <>
                <h3 style={{ marginTop: 0 }}>You need to be logged in to participate</h3>
                <AuthForm onSuccess={(u, freshToken) => confirmGuess(freshToken)} />
                <button onClick={() => setPendingQuote(null)} style={{ marginTop: 10 }}>
                  Cancel
                </button>
              </>
            ) : (
              <>
                <h3 style={{ marginTop: 0 }}>Confirm your guess</h3>
                <p>
                  Guess: <strong>{pendingQuote.guess}</strong>
                  <br />
                  {pendingQuote.isFree ? (
                    <>
                      {/* WHAT IT COSTS AND WHERE IT COMES FROM.
                          This counted free guesses against an allowance
                          — "N of M free guesses remaining" — which was
                          right while the first guess of each competition
                          was silently free.

                          isFree means something different since
                          17 Sep 2026: the entry will be paid FROM FREE
                          CREDIT. The allowance is gone, so baseFreeGuesses
                          and freeLimit are both zero and this rendered
                          "0 of 0 free guesses", which told somebody
                          holding a perfectly good free entry that they
                          had none. */}
                      {(() => {
                        const cost = pendingQuote.creditsPerEntry || 0;
                        const left = Math.max(0, (pendingQuote.freeCredits || 0) - cost);
                        const plural = (n) => (n === 1 ? "" : "s");
                        return (
                          <>
                            <span style={{ color: "var(--accent-green)" }}>
                              Your entry will use{" "}
                              <strong>
                                {cost} free credit{plural(cost)}
                              </strong>
                              , with {left} free credit{plural(left)} remaining
                            </span>
                            {/* THE EXPIRY NOTE ONLY WHEN THERE IS
                                SOMETHING TO LOSE.
                                It used to print regardless, so an entry
                                that spent the lot ended with "0 free
                                credit remaining, and it ends with this
                                competition" — warning about the fate of
                                nothing. */}
                            {left > 0 && (
                              <>
                                <br />
                                <span style={{ fontSize: 12, opacity: 0.75 }}>
                                  Free credit ends with this competition
                                </span>
                              </>
                            )}
                          </>
                        );
                      })()}
                      {pendingQuote.creditsEarnedHere > 0 && (
                          <>
                            <br />
                            <span style={{ fontSize: 12, opacity: 0.85 }}>
                              You have earned {pendingQuote.creditsEarnedHere} credit
                              {pendingQuote.creditsEarnedHere === 1 ? "" : "s"} from spending on
                              this competition
                            </span>
                          </>
                        )}
                    </>
                  ) : (
                    <>
                      {pendingQuote.creditsEnabled &&
                      (pendingQuote.creditBalance || 0) >= (pendingQuote.creditsPerEntry || 0) ? (
                        // ALREADY PAID FOR. Saying "Pay 1.00" to somebody
                        // holding a balance reads as a second charge, and
                        // that is enough to stop them: no card is involved
                        // here, the entry comes out of credit they bought
                        // earlier.
                        <>
                          Uses <strong>{pendingQuote.creditsPerEntry} credit
                            {pendingQuote.creditsPerEntry === 1 ? "" : "s"}</strong> of your
                            balance
                          <br />
                          <span style={{ fontSize: 12, opacity: 0.75 }}>
                            {(pendingQuote.creditBalance || 0) - (pendingQuote.creditsPerEntry || 0)} credits will
                              remain
                          </span>
                        </>
                      ) : (
                        <>
                          {/* WHAT IT COSTS *AND* WHAT IS HELD.
                              This named only the cost, so somebody short by
                              one credit and somebody with none saw the same
                              sentence. The server's own refusal has always
                              said both — "this costs 8 credits and you have
                              3" — and the panel that pre-empts it should
                              not say less. Reported 6 Sep. */}
                          Costs <strong>{pendingQuote.creditsPerEntry} credit
                            {pendingQuote.creditsPerEntry === 1 ? "" : "s"}</strong> and you have{" "}
                          <strong>{pendingQuote.creditBalance || 0}</strong>
                          {/* AND WHAT TO DO ABOUT IT, which differs by site.
                              Stating the shortfall and stopping there is a
                              dead end: on a site selling credit the answer
                              is to top up, and on one that is not, the only
                              route is buying pixels. Saying the wrong one
                              would send them looking for a button that does
                              not exist. */}
                          <br />
                          {/* THE FREE ENTRY, MENTIONED ONLY HERE.

                              Until 17 Sep 2026 the first guess of every
                              competition was silently free, and the confirm
                              step said so — to everyone, whether they had
                              credit or not, and it took the free route
                              whether they wanted it or not.

                              Now the page assumes a paying user and says
                              nothing about it. This branch is reached only
                              when there is not enough credit to enter, which
                              is the one moment the free route is worth
                              raising. */}
                          {pendingQuote.freeEntry?.offered &&
                            !pendingQuote.freeEntry?.claimed && (
                              <>
                                <br />
                                <span style={{ fontSize: 12, color: "var(--accent-green)" }}>
                                  You have a free entry for this competition — take it
                                  from the banner above to add{" "}
                                  {pendingQuote.freeEntry.credits} credit
                                  {pendingQuote.freeEntry.credits === 1 ? "" : "s"}.
                                </span>
                              </>
                            )}
                          <span style={{ fontSize: 12, opacity: 0.75 }}>
                            {pendingQuote.canTopUp
                              ? "Top up your balance in My Account, or earn more by uncovering the image."
                              : "Earn more by uncovering the image — pixels you buy earn credit."}
                          </span>
                        </>
                      )}
                    </>
                  )}
                </p>
                <div style={{ display: "flex", gap: 10, marginTop: 16 }}>
                  <button onClick={() => setPendingQuote(null)} disabled={busy}>
                    Cancel
                  </button>
                  {/* AFFORDABILITY, COMPUTED ONCE.
                      It was worked out twice in this JSX — once for the
                      label and once for the sentence above — and used for
                      neither the disabled state. So the button read "Not
                      enough credit" and was still clickable: the server
                      refused with 402, correctly, and the buyer got an
                      error toast from a control that had already told them
                      it would not work. Nothing was ever charged or
                      counted; it just made a refusal look like a fault. */}
                  {(() => {
                    const affordable =
                      pendingQuote.isFree ||
                      (pendingQuote.creditsEnabled &&
                        (pendingQuote.creditBalance || 0) >= (pendingQuote.creditsPerEntry || 0));
                    // A DEAD END IS NOT A BUTTON.
                    //
                    // This showed a DISABLED button reading "Not enough
                    // credit" — naming the problem and offering nothing.
                    // Where credit can be bought, the answer is a route
                    // to buying it.
                    //
                    // Where it CANNOT (canTopUp off), there is no button
                    // at all: the only way to get credit is uncovering
                    // the image, which the sentence above already says,
                    // and a button leading nowhere would be worse than
                    // none.
                    //
                    // Reported 17 Sep 2026.
                    if (!affordable) {
                      return pendingQuote.canTopUp ? (
                        <button
                          onClick={() => {
                            // THE ACCOUNT TAB, WHERE TopUpControl LIVES.
                            //
                            // Two wrong guesses before this one. First
                            // "/profile#credits" — the tab is a path
                            // segment, not a hash. Then
                            // "/profile/credits", which is the credit
                            // BALANCE and history: the right-sounding
                            // name, and a screen with nothing to buy on
                            // it.
                            //
                            // The purchase control is on the account
                            // tab. Checked, not reasoned about.
                            window.location.href = "/profile/account";
                          }}
                        >
                          Purchase credits
                        </button>
                      ) : null;
                    }
                    return (
                      <button
                        onClick={() => confirmGuess()}
                        disabled={busy || connectionStale}
                      >
                        {busy
                          ? "Submitting…"
                          : pendingQuote.isFree
                            ? "Submit free guess"
                            : // Not "Pay": the money was handed over at
                              // top-up. This spends it.
                              `Use ${pendingQuote.creditsPerEntry} credit${
                                pendingQuote.creditsPerEntry === 1 ? "" : "s"
                              }`}
                      </button>
                    );
                  })()}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// Live feed of recent guesses on the active competition — stacked directly
// underneath StatsPanel, same side of the image, so it uses the identical
// slide convention: tab on the left edge (nearest StatsPanel/the image),
// hiding slides the content left (translateX(-100%)) to tuck it away,
// showing slides it back to translateX(0).
// The "let me know before this starts" mechanism shown on the ended
// screen, alongside the countdown (see reminderShowCountdown/
// reminderCaptureEmail in the backend's buildConfigResponse — both can be
// on together). Picks the right variant based on whether the visitor is
// logged in: a logged-in account already has an email on file, so that's
// just a checkbox to opt in; a logged-out visitor has no email on file at
// all, so that's a manual email field instead.
function ReminderForm({ competitionId, token, subscribed }) {
  return token ? (
    <LoggedInReminderCheckbox competitionId={competitionId} token={token} subscribed={subscribed} />
  ) : (
    <AnonymousReminderForm competitionId={competitionId} />
  );
}

// `subscribed` (1.502.0) is what the server already knows, so the form
// opens as what it IS rather than always offering to start something that
// exists. Asked for on 27 Sep 2026: "if the user has subscribed to receive
// these updates the form says so and provides the option to unsubscribe."
function LoggedInReminderCheckbox({ competitionId, token, subscribed }) {
  const [checked, setChecked] = useState(false);
  // Three states, not two: never asked, just asked, and already on before
  // this page was opened. The last one needs a way back out.
  const [on, setOn] = useState(!!subscribed);
  const [submitted, setSubmitted] = useState(false);
  const [busy, setBusy] = useState(false);
  // The page can refresh under it - a competition ending swaps which one
  // this form is about - so the server's answer wins on every change.
  useEffect(() => {
    setOn(!!subscribed);
    setSubmitted(false);
    setChecked(false);
  }, [subscribed, competitionId]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await wsRequest("reminders.subscribeAsUser", { competitionId }, token);
      setSubmitted(true);
      setOn(true);
    } catch (err) {
      showToast(err.message || "Could not reach backend.", "error");
    }
    setBusy(false);
  };

  const stop = async () => {
    setBusy(true);
    try {
      await wsRequest("reminders.unsubscribeAsUser", { competitionId }, token);
      setOn(false);
      setSubmitted(false);
      setChecked(false);
      showToast("You won't be emailed about future competitions.", "success");
    } catch (err) {
      showToast(err.message || "Could not reach backend.", "error");
    }
    setBusy(false);
  };

  // ALREADY ON, whether from a moment ago or a week ago. One sentence and
  // one way out - a ticked box would invite a submit that does nothing,
  // since subscribing twice is a no-op.
  if (on) {
    return (
      <p data-reminder-on style={{ opacity: 0.8, marginBottom: 10, display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
        <span>
          ✓ {submitted ? "We'll email you" : "You'll get an email"} when future competitions are about to start.
        </span>
        <button type="button" onClick={stop} disabled={busy} className="btn-quiet">
          {busy ? "…" : "Unsubscribe"}
        </button>
      </p>
    );
  }

  return (
    <form onSubmit={submit} style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", marginBottom: 10 }}>
      <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 14, opacity: 0.9 }}>
        <input type="checkbox" checked={checked} onChange={(e) => setChecked(e.target.checked)} />
        Send me an email when future competitions are about to start
      </label>
      <button type="submit" disabled={busy || !checked}>
        {busy ? "…" : "Submit"}
      </button>
    </form>
  );
}

function AnonymousReminderForm({ competitionId }) {
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await wsRequest("reminders.subscribe", { competitionId, email });
      setSubmitted(true);
    } catch (err) {
      showToast(err.message || "Could not reach backend.", "error");
    }
    setBusy(false);
  };

  const inputStyle = {
    padding: 8,
    background: "var(--bg-input)",
    border: "1px solid var(--border-color-light)",
    borderRadius: 6,
    color: "var(--text-primary)",
  };

  if (submitted) {
    return (
      <p style={{ opacity: 0.8, maxWidth: 700 }}>
        ✓ Thanks — we'll email you when future competitions are about to start.
      </p>
    );
  }

  return (
    <div style={{ maxWidth: 700, marginBottom: 10 }}>
      <form onSubmit={submit} style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
        <input
          type="email"
          required
          placeholder="you@example.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          style={{ ...inputStyle, flex: "1 1 240px" }}
        />
        <button type="submit" disabled={busy}>
          {busy ? "…" : "Remind me"}
        </button>
      </form>
    </div>
  );
}

// Tracks connection status for a periodically-polled panel: shows nothing
// during normal operation, "connecting" (calm, not alarming) while a poll
// is failing, then briefly flashes green "Connected" right after a poll
// succeeds again following a failure, fading out on its own afterward.
// Centralizes the timing so every panel that uses it behaves consistently.
function useConnectionStatus(hasError) {
  const [status, setStatus] = useState(null); // null | "connecting" | "connected"
  const [visible, setVisible] = useState(false);
  const wasErroring = useRef(false);
  useEffect(() => {
    if (hasError) {
      wasErroring.current = true;
      setStatus("connecting");
      setVisible(true);
    } else if (wasErroring.current) {
      wasErroring.current = false;
      setStatus("connected");
      setVisible(true);
      const fadeTimer = setTimeout(() => setVisible(false), 1500); // start fading out
      const removeTimer = setTimeout(() => setStatus(null), 2000); // fully gone once the fade's had time to finish
      return () => {
        clearTimeout(fadeTimer);
        clearTimeout(removeTimer);
      };
    }
  }, [hasError]);
  return { status, visible };
}

function ConnectionStatusBanner({ status, visible }) {
  if (!status) return null;
  const connected = status === "connected";
  return (
    <div
      style={{
        fontSize: 12,
        padding: "4px 8px",
        borderRadius: 4,
        marginBottom: 8,
        textAlign: "center",
        transition: "opacity 0.5s ease, background-color 0.4s ease, color 0.4s ease",
        opacity: visible ? 1 : 0,
        background: connected ? "var(--toast-success-bg)" : "var(--bg-panel-alt)",
        color: connected ? "var(--toast-success-icon)" : "var(--text-secondary)",
      }}
    >
      {connected ? "Connected" : "Connecting…"}
    </div>
  );
}

function LastEntriesPanel({ competitionId }) {
  const [entries, setEntries] = useState(null);
  const [error, setError] = useState(null);
  const { status, visible } = useConnectionStatus(!!error);

  useEffect(() => {
    if (!competitionId) return;
    const load = () => {
      wsRequest("entries.recent")
        .then((data) => {
          setEntries(data);
          setError(null);
        })
        .catch(() => setError(true));
    };
    load();
    const id = setInterval(load, 5000);
    return () => clearInterval(id);
  }, [competitionId]);

  return (
    <div style={{ width: "100%", maxWidth: "var(--sidebar-max-width)", border: "1px solid var(--border-color)", borderRadius: 8, padding: 12, boxSizing: "border-box", color: "var(--text-primary)" }}>
      <div style={{ fontSize: 13, opacity: 0.6, marginBottom: 8 }}>Last entries</div>
      <ConnectionStatusBanner status={status} visible={visible} />
      {entries === null ? (
        error ? null : <p style={{ fontSize: 13, opacity: 0.7 }}>Loading…</p>
      ) : entries.length === 0 ? (
        <p style={{ fontSize: 13, opacity: 0.7 }}>No entries yet — be the first to guess!</p>
      ) : (
        entries.map((e) => (
          <div
            key={e.id}
            style={{ fontSize: 12, marginBottom: 8, paddingBottom: 8, borderBottom: "1px solid var(--border-color-subtle)" }}
          >
            <div>
              <strong>{e.displayName}</strong>{" "}
              <span style={{ color: e.correct ? "var(--accent-green)" : "var(--text-secondary)" }}>
                {e.correct ? `guessed "${e.guess}" — correct!` : `guessed "${e.guess}"`}
              </span>
            </div>
            <div style={{ opacity: 0.5 }}>{siteTime(e.createdAt)}</div>
          </div>
        ))
      )}
    </div>
  );
}

function StatsPanel({ token, competitionId }) {
  // Needed for the entry-credit line below. StatsPanel had no currency of
  // its own until then — other components call this same hook, so an
  // earlier draft referenced `symbol` here and COMPILED FINE while being a
  // guaranteed ReferenceError the moment credits were switched on.
  const symbol = useCurrencySymbol();
  const [stats, setStats] = useState(null);
  const [competitionsEntered, setCompetitionsEntered] = useState(null);
  const [publicStats, setPublicStats] = useState(null);
  const [error, setError] = useState(null);
  const { status, visible } = useConnectionStatus(!!error);

  // Public, competition-wide numbers — polled on their own cadence,
  // independent of login status and of however often the parent page
  // happens to refresh its own config (which only reloads on specific
  // triggers like a correct guess, not on every guess/purchase).
  useEffect(() => {
    const load = () => {
      wsRequest("config.get")
        .then((data) => {
          if (!data || data.status !== "active") return;
          setPublicStats({
            imagesRevealed: data.imagesRevealed,
            usersEnteredCompetition: data.usersEnteredCompetition,
            entriesSubmittedCompetition: data.entriesSubmittedCompetition,
          });
        })
        .catch(() => {});
    };
    load();
    const id = setInterval(load, 5000);
    return () => clearInterval(id);
  }, [competitionId]);

  const loadMyStats = () => {
    if (!token || !competitionId) return;
    wsRequest("guess.status", {}, token)
      .then((data) => {
        setStats(data);
        setError(null);
      })
      .catch(() => setError(true));

    wsRequest("auth.myEntries", {}, token)
      .then((data) => {
        setCompetitionsEntered(new Set(data.map((e) => e.competitionId)).size);
      })
      .catch(() => {});
  };

  useEffect(() => {
    if (!token || !competitionId) return;
    loadMyStats();
    const id = setInterval(loadMyStats, 5000);
    return () => clearInterval(id);
  }, [token, competitionId]);

  return (
    <>
      <div style={{ width: "100%", maxWidth: "var(--sidebar-max-width)", border: "1px solid var(--border-color)", borderRadius: 8, padding: 12, boxSizing: "border-box", color: "var(--text-primary)" }}>
        <div style={{ fontSize: 13, opacity: 0.6, marginBottom: 4 }}>This competition</div>
        <div style={{ fontSize: 14, marginBottom: 2 }}>
          Images revealed: {publicStats ? publicStats.imagesRevealed : "…"}
        </div>
        <div style={{ fontSize: 14, marginBottom: 2 }}>
          Users on this competition: {publicStats ? publicStats.usersEnteredCompetition : "…"}
        </div>
        <div style={{ fontSize: 14 }}>
          Entries submitted: {publicStats ? publicStats.entriesSubmittedCompetition : "…"}
        </div>
      </div>

      <div style={{ width: "100%", maxWidth: "var(--sidebar-max-width)", border: "1px solid var(--border-color)", borderRadius: 8, padding: 12, boxSizing: "border-box", color: "var(--text-primary)" }}>
        {!token ? (
          <p style={{ fontSize: 13, opacity: 0.8, margin: 0 }}>Log in above to see your own entry stats.</p>
        ) : (
          <>
            <ConnectionStatusBanner status={status} visible={visible} />
            {!stats ? (
              error ? null : <p style={{ fontSize: 13, opacity: 0.7, margin: 0 }}>Loading…</p>
            ) : (
              <>
                <div style={{ fontSize: 13, opacity: 0.6, marginBottom: 4 }}>My stats</div>
                <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 10 }}>
                  {/* WHAT THE BALANCE BUYS, worked out by the server
                      (entriesAvailable). This was "N of M" against the
                      free-guess allowance, which is 0 for everybody
                      since 17 Sep 2026 — so it read "0 of 0" whatever
                      the buyer held. */}
                  Current entries available: {stats.entriesAvailable ?? "—"}
                </div>
                <div style={{ fontSize: 14 }}>
                  Number of competitions entered: {competitionsEntered === null ? "…" : competitionsEntered}
                </div>
                {/* ENTRY CREDIT. Shown whenever the feature is enabled,
                    including at zero — a buyer who cannot see the balance
                    has no way to know the facility exists. Topping up is
                    done from My Account, deliberately not duplicated here:
                    this panel reports state, it does not take payments. */}
                {stats.creditsEnabled && (
                  <div style={{ fontSize: 14, marginTop: 4 }}>
                    Entry credit: {stats.creditBalance || 0} credit{(stats.creditBalance || 0) === 1 ? "" : "s"}
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>
    </>
  );
}

export default function App({ demoRouteToken } = {}) {
  const symbol = useCurrencySymbol();
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  const [pageMode, setPageMode] = useState("loading"); // "loading" | "active" | "ended" | "public-site-disabled" | "none"
  const [endedInfo, setEndedInfo] = useState(null); // { competitionId, title, answer, answeredByEmail, imageWidth, imageHeight, prizeMoney, next }
  const [endedImageUrl, setEndedImageUrl] = useState(null);
  const [noneInfo, setNoneInfo] = useState(null); // { upcoming, queueRunning, showCountdown, captureEmail }
  const [publicImageUrl, setPublicImageUrl] = useState(null);
  const [userToken, setUserToken] = useState(() => localStorage.getItem(USER_TOKEN_KEY));
  const [authUser, setAuthUser] = useState(null);
  // Set only when the buyer has just landed back from a real payment
  // provider's own hosted checkout page (see confirmPurchase below) —
  // "checking" while polling for the webhook's own, independent
  // confirmation to land, then "confirmed"/"failed" once it does.
  const [pendingPurchaseStatus, setPendingPurchaseStatus] = useState(null);

  // Registers this page's demo-route token (if any) with wsClient's own
  // module-level state, so every wsRequest call made anywhere in this
  // component automatically carries it — see setDemoRouteToken's own
  // comment in wsClient.js for why this is ambient page context rather
  // than something threaded through each individual call site. Cleared
  // on unmount as explicit defense-in-depth (navigating elsewhere in the
  // app would already self-correct this on the next mount regardless,
  // since this effect re-runs whenever the prop itself changes).
  useEffect(() => {
    setDemoRouteToken(demoRouteToken);
    // NO CLEANUP THAT CLEARS IT. It used to clear on unmount as
    // defence-in-depth, which was right while only this component knew the
    // mode. The mode now belongs to the TAB (see previewMode.js) and every
    // page registers the same token, so clearing here would mean navigating
    // to My Account briefly dropped the site back to Live and depended on
    // React running the next page's effect afterwards to put it back.
    //
    // Leaving it set is not a leak: main.jsx passes the same value to
    // whichever page mounts next, and leaving preview mode is a full page
    // load that clears the session outright.
    //
    // The deliberate clear when a demo link EXPIRES is further down and
    // still stands — that is a revocation, not a navigation.
  }, [demoRouteToken]);

  // Picks up where confirmPurchase's real-payment-provider branch left
  // off — the buyer's browser lands back here (via the backend's own
  // /api/v1/purchase/return redirect) with ?pendingPurchase=<id> in the
  // URL, regardless of whether the payment actually succeeded; the
  // webhook (a separate request, not this one) is the real source of
  // truth, so this polls for it rather than trusting anything about
  // how the buyer got redirected back.
  useEffect(() => {
    const pendingId = new URLSearchParams(window.location.search).get("pendingPurchase");
    if (!pendingId) return;
    // Strips the query param immediately so a later manual refresh of
    // this same page doesn't re-trigger polling for an already-settled
    // purchase.
    window.history.replaceState({}, "", window.location.pathname);
    setPendingPurchaseStatus("checking");
    let cancelled = false;
    const poll = async () => {
      for (let attempt = 0; attempt < 30 && !cancelled; attempt++) {
        try {
          const res = await fetch(`${API_BASE}/api/v1/purchase/start/${pendingId}/status`, {
            // Without this the owner polls ANONYMOUSLY and the endpoint's
            // ownership gate withholds their own result - so the tile
            // they just paid for never comes back to them.
            headers: userToken ? { "x-auth-token": userToken } : {},
          });
          const data = await res.json();
          if (data.status === "confirmed") {
            setPendingPurchaseStatus("confirmed");
            // Register the tile as THIS buyer's, so the private overlay
            // and its countdown appear. checkMyRegions cannot do this:
            // it only flips regions already known to be mine, and never
            // adds one. The direct purchase path adds its region inline
            // - this is the webhook path's equivalent.
            if (data.result && data.rect) {
              setMyRegions((prev) =>
                prev.some((r) => r.ownerToken === data.result.ownerToken)
                  ? prev
                  : [
                      ...prev,
                      {
                        ownerToken: data.result.ownerToken,
                        competitionId: data.competitionId,
                        ...data.rect,
                        tile: data.result.tile,
                        privateUntil: data.result.privateUntil,
                        isPublic: false,
                      },
                    ]
              );
            }
            // THE RIGHT SENTENCE FOR WHAT WAS BOUGHT.
            //
            // A credit top-up returns through the same route with the same
            // `pendingPurchase` parameter as a pixel purchase, so this said
            // "your pixels are yours" to somebody who had just bought
            // credit. Reported 6 Sep.
            showToast(
              data.isTopUp
                ? "Payment confirmed — your credit has been added."
                : "Payment confirmed — your pixels are yours!",
              "success"
            );
            // No manual refresh needed here — executePurchase (called by
            // the webhook that just confirmed this) already broadcasts a
            // "purchase" WS event unconditionally, which the existing WS
            // handler further below already uses to refresh the image
            // and myRegions for this client automatically.
            return;
          }
          // EVERY SETTLED STATUS, from the table above. This was
          // `data.status === "failed"` alone, so the four other settled
          // statuses carried on polling to the thirty-attempt give-up.
          //
          // THE STATE IS SET TO THE STATUS ITSELF rather than to "failed"
          // for all of them. Nothing renders it - both readers test
          // `=== "checking"` - so this changes nothing on screen today and
          // means a reader that wants to tell them apart can.
          const settled = PURCHASE_SETTLED_MESSAGES[data.status];
          if (settled) {
            setPendingPurchaseStatus(data.status);
            showToast(settled.text(pendingId), settled.type);
            return;
          }
        } catch {
          // Transient network hiccup — keep polling rather than giving
          // up on the first failed check.
        }
        await new Promise((resolve) => setTimeout(resolve, 2000));
      }
      if (!cancelled) {
        // "unsettled", not "failed". Sixty seconds without an answer is
        // not a verdict, and the sentence no longer implies one.
        setPendingPurchaseStatus("unsettled");
        showToast(PURCHASE_UNSETTLED_MESSAGE(pendingId), "warning");
      }
    };
    poll();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sole source of auth state now that SiteHeader is rendered once,
  // globally, in main.jsx rather than per-page — this subscription is
  // what keeps authUser/userToken in sync with it, covering every case
  // (initial token verification on load, login/signup through the
  // header's own dropdown, login through the in-page "you need to be
  // logged in" modal below, and logout) equally, since SiteHeader
  // broadcasts through this same channel regardless of which of those
  // triggered the change.
  useEffect(
    () =>
      onAuthChanged((u, t) => {
        setAuthUser(u);
        setUserToken(t);
      }),
    []
  );
  // A genuinely invalidated session (see onSessionInvalid's own comment
  // in wsClient.js) — e.g. a tab left open from before a fresh install
  // wiped every account, still showing as "logged in" with a token the
  // backend no longer recognizes at all. Routed through the same
  // announceAuthChange channel a normal logout already uses, so every
  // listener (this component, SiteHeader) clears together rather than
  // needing its own separate handling.
  useEffect(
    () =>
      onSessionInvalid(() => {
        announceAuthChange(null, null);
        showToast("Your session is no longer valid — please log in again.", "error");
      }),
    []
  );
  const [myRegions, setMyRegions] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEY)) || [];
    } catch {
      return [];
    }
  });
  const [drag, setDrag] = useState(null); // {startX, startY, x, y, w, h} in image coords
  const [pendingQuote, setPendingQuote] = useState(null); // {rect, price, available}
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const { status: connectionStatus, visible: connectionVisible } = useConnectionStatus(!!error);
  // loadConfig runs over the WebSocket; purchases go over HTTP. When the
  // socket is down the page cannot refresh the competition, but the
  // HTTP side may be perfectly healthy - so without this it keeps
  // offering Pay against a competition that may have ended minutes ago,
  // and every click is refused by the server.
  //
  // `error` is set by loadConfig's own catch, so this is true exactly
  // when the displayed competition can no longer be trusted.
  const connectionStale = !!error;

  // ---- selection assist tools (mobile-friendly alternatives to free-hand dragging) ----
  // Pick for me and Fixed-size drag share one underlying mechanism: a
  // box (and arrow) the user can see and drag before confirming, rather
  // than committing to a quote immediately. They differ only in how the
  // initial position is chosen - both search for an available spot since
  // 1.558.0, and only Fixed-size drag tries the centre first (see
  // findAssistPosition) - and which toggle button reflects the
  // currently-active tool.
  const [activeTool, setActiveTool] = useState(null); // null | "pick" | "fixed"
  const [toolRect, setToolRect] = useState(null); // {x, y} top-left in image coords; size comes from assistSize
  const [toolDragOffset, setToolDragOffset] = useState(null); // {dx, dy} from the box's top-left to where the user grabbed it

  // ---- UK compliance "no purchase necessary" free entry ----
  // A single fixed 1x1px pixel, chosen by clicking (not dragging) while
  // this mode is active — deliberately a separate interaction from the
  // normal drag-select/activeTool above, since it's a one-shot, one-per-
  // competition pick rather than an adjustable selection.
  const [freeEntryMode, setFreeEntryMode] = useState(false); // true = the next click on the image picks the free pixel
  const [pendingFreeEntry, setPendingFreeEntry] = useState(null); // {x, y} once picked, awaiting confirmation

  const containerRef = useRef(null);
  const canvasRef = useRef(null);
  const tileImagesRef = useRef({}); // cache of decoded <img> for private tiles

  // The image + side-panels row's width isn't fixed — it depends on the
  // current competition's image dimensions plus the panel column — so
  // this is derived directly from the image's known native width rather
  // than measured from the DOM, and use that same width for the sections
  // above/below it (title, the two boxes, the guess form) so their edges
  // actually line up with the image and panels. (A previous version
  // measured the image row's own rendered width via ResizeObserver, but
  // that row's width is itself constrained by this same contentWidth
  // through its parent wrapper — a circular dependency that, once it
  // locked onto a too-wide value from an unconstrained initial render,
  // never corrected itself, leaving a large stray gap on wide screens.)
  const contentWidth = config ? config.imageWidth + 276 : 900; // sidebar (260) + gap (16) between it and the image

  // The guess form is fixed to the bottom of the viewport (see its wrapper
  // below) so it stays reachable while scrolling instead of scrolling
  // away — but a fixed element sits on top of whatever's normally behind
  // it, so without this the page's last bit of content (the sidebar
  // boxes) would end up permanently hidden underneath it. Measuring the
  // form's actual rendered height and using that as bottom padding on the
  // page keeps everything reachable regardless of how tall the form ends
  // up being at a given screen width.
  const guessFormRef = useRef(null);
  const [guessFormHeight, setGuessFormHeight] = useState(0);
  useEffect(() => {
    if (!guessFormRef.current) return;
    const el = guessFormRef.current;
    const update = () => setGuessFormHeight(el.offsetHeight || 0);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, [config?.competitionId]);

  // How much the user wants to spend per selection — set on "My account" →
  // Competition Preferences, so this feature is login-only now (that's also
  // what simplified this down to a plain derived value: no more separate
  // state/effect/localStorage fallback for anonymous users to keep in sync).
  const preferredSpend = authUser?.defaultSpendAmount || 0;

  // The side length of a roughly-square selection costing about what the
  // user said they want to spend, at the active competition's current
  // price per pixel. Falls back to the competition's minimum region size
  // if no spend preference has been set yet.
  const assistSize =
    config && preferredSpend > 0
      ? Math.max(config.minRegion, Math.round(Math.sqrt(preferredSpend / config.pricePerPixel)))
      : config?.minRegion || 10;

  // Looks for a fully-available spot (no already-purchased/held pixels at
  // all) before settling for one that's only partially available — tries
  // the center first (cheapest common case), then several random
  // positions (deliberately random rather than a fixed set of spots like
  // the corners, so it doesn't keep landing in the same predictable
  // places), checking each via the same quote endpoint the manual confirm
  // flow already uses (the client has no direct visibility into *other*
  // buyers' regions, so this is the only accurate way to check from
  // here). Stops at the first fully-available candidate; if none turn out
  // fully open, falls back to whichever candidate had the fewest
  // already-claimed pixels.
  // BOTH ASSIST TOOLS LOOK FOR SOMEWHERE WORTH BUYING (1.558.0).
  //
  // Asked for on 2 Oct 2026: "Pick for me should pick an area that has not
  // been revealed."
  //
  // IT USED TO BE THE OTHER WAY ROUND. Fixed-size drag searched for an
  // available spot; PICK FOR ME - the one a buyer presses precisely
  // because they want the choice made for them - picked at random and
  // checked nothing. Its comment argued that the pricing handles
  // already-public and already-privately-held pixels correctly wherever it
  // lands, so the pick did not need to care.
  //
  // TRUE ABOUT THE PRICE, BESIDE THE POINT. A fully-revealed area quotes
  // newPixels: 0, which shows "already fully revealed or already yours"
  // and DISABLES the Buy button. On a well-uncovered competition the
  // feature whose only job is to choose hands back something unbuyable.
  //
  // ONE SEARCH NOW, with one difference: Fixed-size drag tries the CENTRE
  // first (cheapest common case, and a predictable box for somebody about
  // to drag it), and Pick for me does NOT - trying the centre first would
  // return the same spot every time while it stayed available, and a dice
  // button that always picks the middle is not picking.
  //
  // The widget carries the same function, for the same reason, against the
  // same quote.
  const findAssistPosition = async ({ centreFirst }) => {
    if (!config) return null;
    const maxX = Math.max(0, config.imageWidth - assistSize);
    const maxY = Math.max(0, config.imageHeight - assistSize);
    const totalPixels = assistSize * assistSize;
    const ownerTokens = myRegions.map((r) => r.ownerToken);

    const candidates = centreFirst ? [{ x: Math.round(maxX / 2), y: Math.round(maxY / 2) }] : [];
    for (let i = 0; i < (centreFirst ? 5 : 6); i++) {
      candidates.push({ x: Math.round(Math.random() * maxX), y: Math.round(Math.random() * maxY) });
    }

    let best = null;
    for (const pos of candidates) {
      try {
        const data = await wsRequest(
          "quote.get",
          { x: pos.x, y: pos.y, w: assistSize, h: assistSize, ownerTokens },
          userToken
        );
        if (data.newPixels === totalPixels) return pos; // fully available — good enough, stop here
        if (!best || data.newPixels > best.newPixels) best = { ...pos, newPixels: data.newPixels };
      } catch {
        // couldn't check this one — just move on to the next candidate
      }
    }
    // The least-claimed candidate rather than a refusal: late in a
    // competition there may be nothing fully open left, and the quote
    // dialog then says what is already paid for.
    return best ? { x: best.x, y: best.y } : candidates[0];
  };

  // Give the active tool's box a sensible starting position — a
  // fully-available one, or the least-claimed candidate tried, for either
  // tool — the moment a tool is turned on, and clean up when it's turned
  // off. Without this it would stay
  // null forever, since nothing else ever sets an initial position for
  // it.
  // Re-finds a position whenever assistSize actually changes (tracked via
  // ref, since useEffect's dependency array alone can't tell which
  // dependency changed) — not just when toolRect is empty. Without the
  // size-changed check, a size change alone (e.g. the preferred spend
  // amount updating) with the box already placed wouldn't re-trigger this
  // branch at all: it only ran when toolRect was empty, leaving the box
  // sitting at a position computed for the old size while its drawn
  // dimensions (which read assistSize live, not a snapshot) visually
  // changed size around that now-stale spot. Same idea for switching
  // directly from one tool to the other while already active — toolRect
  // is already non-null from whichever tool was active before, so
  // without also tracking which tool that was, switching straight from
  // Pick for me to Fixed-size drag (or back) would silently keep the
  // previous tool's position instead of running the new tool's own
  // position-finding logic.
  const prevAssistSizeRef = useRef(assistSize);
  const prevActiveToolRef = useRef(activeTool);
  useEffect(() => {
    const sizeChanged = prevAssistSizeRef.current !== assistSize;
    const toolChanged = prevActiveToolRef.current !== activeTool;
    prevAssistSizeRef.current = assistSize;
    prevActiveToolRef.current = activeTool;
    if (activeTool && (!toolRect || sizeChanged || toolChanged) && config) {
      let cancelled = false;
      // Centre first only for the tool about to be dragged; see
      // findAssistPosition for why Pick for me must not.
      findAssistPosition({ centreFirst: activeTool !== "pick" }).then((pos) => {
        if (!cancelled && pos) setToolRect(pos);
      });
      return () => {
        cancelled = true;
      };
    }
    if (!activeTool) {
      setToolRect(null);
      setToolDragOffset(null);
    }
  }, [activeTool, config, assistSize]);

  // ticks once a second purely to force the canvas countdown text to redraw
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, []);

  // Computed locally from the countdown, not read directly off
  // config.isLottery — that field is only as fresh as the last loadConfig()
  // call (a 60s fallback poll), so relying on it directly meant this
  // wouldn't flip until the next poll or a manual refresh. lotteryConvertAt
  // is a fixed point in time the server already told us about, so deriving
  // "have we passed it yet" locally on every tick is both more accurate
  // and needs no extra network round-trip at all.
  const isLottery =
    config?.isLottery || (config?.lotteryConvertAt != null && serverNow() >= config.lotteryConvertAt);

  // ---- load config once, and keep retrying so the page picks up the queue
  // advancing (active -> ended -> next active) without a manual refresh ----
  const loadConfig = useCallback(() => {
    wsRequest("config.get", {}, userToken)
      .catch((wsErr) => {
        // config.get fell over, which in practice means the socket is
        // down. The HTTP route is the same builder and fails
        // independently, so try it before giving up - otherwise the page
        // simply stops updating and keeps showing a competition that may
        // have ended.
        //
        // Rethrows on the demo route: /api/config has no demo-route
        // token, so it would return the live view, which on a demo page
        // is worse than stale.
        if (demoRouteToken) throw wsErr;
        return fetch(`${API_BASE}/api/config`, {
          headers: userToken ? { "x-auth-token": userToken } : {},
        }).then((r) => (r.ok ? r.json() : Promise.reject(wsErr)));
      })
      .then((data) => {
        setError(false);
        // The SITE's timezone, before anything renders a date. Every
        // formatter in siteTime.js reads it, so one call here settles the
        // whole page — and a viewer abroad sees the announced time rather
        // than their own clock.
        setSiteTimeZone(data.siteTimeZone);
        if (data.status === "demo-expired") {
          // Cleared synchronously here rather than relying solely on the
          // registration effect's own cleanup below (which only runs
          // after React's next render pass) — this fetch's own re-call
          // needs the module-level state already cleared, or it could
          // still carry the stale token and loop right back into
          // "expired" again.
          setDemoRouteToken(null);
          showToast("This demo preview link has expired.", "error");
          navigate("/");
          loadConfig();
          return;
        }
        // Align with the server before anything counts down.
        setServerClockOffset(data?.serverTime);
        if (data.status === "active") {
          setPageMode("active");
          setConfig(data);
          setEndedInfo(null);
          setNoneInfo(null);
        } else if (data.status === "ended") {
          setPageMode("ended");
          setEndedInfo(data);
          setConfig(null);
          setNoneInfo(null);
        } else if (data.status === "maintenance") {
          // Not in Live mode, and this visitor is not one of the
          // operator's own people. Deliberately says nothing about WHY —
          // not the mode, not that a domain rule exists. A maintenance
          // notice is an ordinary thing for a site to show; "you are not
          // on the list" tells a stranger there is a list.
          setPageMode("maintenance");
          setConfig(null);
          setEndedInfo(null);
          setNoneInfo(null);
        } else if (data.status === "public-site-disabled") {
          setPageMode("public-site-disabled");
          setConfig(null);
          setEndedInfo(null);
          setNoneInfo(null);
        } else {
          setPageMode("none");
          setNoneInfo(data);
          setConfig(null);
          setEndedInfo(null);
        }
      })
      .catch(() => setError(true));
  }, [userToken, navigate, demoRouteToken]);

  useEffect(() => {
    loadConfig();
    const id = setInterval(loadConfig, 60000); // fallback only, for a dropped WS connection — the listener below is what actually keeps this current
    return () => clearInterval(id);
  }, [loadConfig]);

  // Live-updates on any competitions-changed broadcast (another player
  // winning, an admin edit, the queue advancing, etc) — without this the
  // only things that ever refreshed config were this browser's own correct
  // guess and the 60s fallback poll above, so anything caused by someone
  // else could sit stale for up to a minute.
  useEffect(() => {
    return onBroadcast((message) => {
      if (message.type === "competitions-changed") loadConfig();
    });
  }, [loadConfig]);

  // ---- a competition ending while someone is mid-selection ----
  //
  // The server refuses a purchase against a competition that is no
  // longer current (409), so nobody can be charged for the wrong one.
  // But a refusal at the moment they click is a poor way to find out:
  // they have chosen a rectangle on an image, been quoted a price, and
  // the Buy button is sitting there looking live.
  //
  // Worse on the hosted checkout, where clicking Buy sends them to a
  // payment page. Better to clear the selection the instant the
  // competition changes and say why, than to let them start paying for
  // something that has already finished.
  //
  // The ref is so this only fires on a genuine CHANGE — on first load
  // the id goes from undefined to a value, which is not a competition
  // ending under anyone.
  const lastCompetitionIdRef = useRef(null);
  useEffect(() => {
    const previous = lastCompetitionIdRef.current;
    const now = config?.competitionId ?? null;
    lastCompetitionIdRef.current = now;
    if (!previous || previous === now) return;

    // Only worth interrupting them if they had something in progress.
    if (pendingQuote || drag || toolRect || pendingFreeEntry) {
      setPendingQuote(null);
      setDrag(null);
      setToolRect(null);
      setPendingFreeEntry(null);
      setActiveTool(null);
      showToast(
        "That competition finished while you were choosing — your selection has been cleared. The new competition is a different picture, so pick again.",
        "info"
      );
    }
  }, [config?.competitionId, pendingQuote, drag, toolRect, pendingFreeEntry]);

  // ---- persist myRegions ----
  useEffect(() => {
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(myRegions));
  }, [myRegions]);

  // ---- poll the server-composited public image for the current competition ----
  const refreshPublicImage = useCallback((competitionId) => {
    if (!competitionId) return;
    // The fog pattern is a static server-rendered image with the text
    // baked into its pixels — CSS can't resize that text after the fact,
    // so a genuinely larger-text version has to be requested outright on
    // a narrow screen rather than just scaled down from the desktop one.
    const variant = window.innerWidth <= 768 ? "&variant=mobile" : "";
    setPublicImageUrl(`${API_BASE}/api/competitions/${competitionId}/public-image?t=${Date.now()}${variant}`);
  }, []);

  useEffect(() => {
    if (!config?.competitionId) return;
    refreshPublicImage(config.competitionId);
    const id = setInterval(() => refreshPublicImage(config.competitionId), 20000); // fallback only — WS pushes purchase events in real time
    return () => clearInterval(id);
  }, [config?.competitionId, refreshPublicImage]);

  // ---- fetch the fully-revealed image once a competition has ended ----
  // Static once fetched — the image doesn't change further, so no polling.
  useEffect(() => {
    if (pageMode !== "ended" || !endedInfo?.competitionId) return;
    setEndedImageUrl(`${API_BASE}/api/competitions/${endedInfo.competitionId}/public-image?t=${Date.now()}`);
  }, [pageMode, endedInfo?.competitionId]);

  // ---- periodically re-check my private regions against the server ----
  const checkMyRegions = useCallback(() => {
    setMyRegions((current) => {
      current.forEach((region) => {
        if (region.isPublic) return;
        wsRequest("region.mine", { competitionId: region.competitionId, ownerToken: region.ownerToken }, userToken)
          .then((data) => {
            if (data.isPublic) {
              setMyRegions((prev) =>
                prev.map((r) => (r.ownerToken === region.ownerToken ? { ...r, isPublic: true } : r))
              );
            }
          })
          .catch(() => {});
      });
      return current; // this call only reads current regions, doesn't change them
    });
  }, [userToken]);

  useEffect(() => {
    if (!myRegions.length) return;
    const id = setInterval(checkMyRegions, 3000);
    return () => clearInterval(id);
  }, [myRegions.length, checkMyRegions]);

  // ---- WebSocket: real-time push so purchases and competition changes show
  // up immediately instead of waiting for the next poll. Falls back fine to
  // the polling above if the connection drops — this is purely an upgrade,
  // not a replacement, so a flaky WS never leaves the page stuck stale. ----
  useEffect(() => {
    let ws;
    let reconnectTimer;

    const connect = () => {
      ws = new WebSocket(`${window.location.protocol === "https:" ? "wss" : "ws"}://${window.location.host}/ws`);
      ws.onmessage = (event) => {
        let message;
        try {
          message = JSON.parse(event.data);
        } catch {
          return;
        }
        if (message.type === "purchase" && message.competitionId === config?.competitionId) {
          refreshPublicImage(message.competitionId);
          checkMyRegions();
        }
        if (message.type === "competitions-changed") {
          loadConfig();
        }
      };
      ws.onclose = () => {
        reconnectTimer = setTimeout(connect, 3000); // simple retry — good enough for a demo
      };
    };
    connect();

    return () => {
      clearTimeout(reconnectTimer);
      if (ws) {
        ws.onclose = null; // don't reconnect on our own intentional cleanup
        ws.close();
      }
    };
  }, [config?.competitionId, refreshPublicImage, checkMyRegions, loadConfig]);

  // ---- redraw canvas overlay: selection box + my still-private tiles ----
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !config) return;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // draw my private (not-yet-public) tiles, since the shared public-image
    // endpoint deliberately does NOT include them for anyone but me
    myRegions
      .filter((r) => !r.isPublic)
      .forEach((r) => {
        let img = tileImagesRef.current[r.ownerToken];
        if (!img) {
          img = new Image();
          img.src = r.tile;
          tileImagesRef.current[r.ownerToken] = img;
        }
        if (img.complete) {
          ctx.drawImage(img, r.x, r.y, r.w, r.h);
        } else {
          img.onload = () => ctx.drawImage(img, r.x, r.y, r.w, r.h);
        }

        const msLeft = r.privateUntil - serverNow();
        if (msLeft > 0) {
          ctx.fillStyle = "#ffe66d";
          ctx.font = "12px sans-serif";
          ctx.fillText(`private • ${formatCountdown(msLeft)}`, r.x + 2, r.y - 4);
        }
      });

    // draw the crosshair / live selection rectangle
    if (drag) {
      ctx.strokeStyle = "#00d1ff";
      ctx.lineWidth = 1;
      ctx.strokeRect(drag.x, drag.y, drag.w, drag.h);
      ctx.beginPath();
      ctx.moveTo(drag.x + drag.w / 2, 0);
      ctx.lineTo(drag.x + drag.w / 2, config.imageHeight);
      ctx.moveTo(0, drag.y + drag.h / 2);
      ctx.lineTo(config.imageWidth, drag.y + drag.h / 2);
      ctx.strokeStyle = "rgba(0,209,255,0.3)";
      ctx.stroke();
    }

    // A small arrow pointing at a rect — assistSize can be quite small
    // relative to a large hidden image, easy to miss otherwise. Drawn on
    // whichever side (above/below) has more room, so it's never cut off
    // near an edge. Built tip-first: the tip sits a clear gap away from
    // the rect (not touching it), the arrowhead's base sits behind the
    // tip, and the shaft sits behind that — each segment stops where the
    // next one starts, so nothing overshoots or overlaps.
    const drawArrowAtRect = (rect, color) => {
      const centerX = rect.x + rect.w / 2;
      const spaceAbove = rect.y;
      const spaceBelow = config.imageHeight - (rect.y + rect.h);
      const pointFromAbove = spaceAbove >= spaceBelow;

      const tipY = pointFromAbove ? rect.y - ARROW_GAP : rect.y + rect.h + ARROW_GAP;
      const baseY = pointFromAbove ? tipY - ARROW_HEAD_LEN : tipY + ARROW_HEAD_LEN;
      const tailY = pointFromAbove ? baseY - ARROW_SHAFT_LEN : baseY + ARROW_SHAFT_LEN;

      ctx.save();
      ctx.strokeStyle = color;
      ctx.fillStyle = color;
      ctx.lineWidth = 4;
      ctx.setLineDash([]);
      ctx.beginPath();
      ctx.moveTo(centerX, tailY);
      ctx.lineTo(centerX, baseY);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(centerX, tipY);
      ctx.lineTo(centerX - ARROW_HEAD_WIDTH, baseY);
      ctx.lineTo(centerX + ARROW_HEAD_WIDTH, baseY);
      ctx.closePath();
      ctx.fill();
      ctx.restore();
    };

    // draw the active tool's assist box — solid, not dashed: a dash
    // pattern doesn't align cleanly with a short perimeter at small
    // assistSize values, which was making 1-2 sides render with no
    // visible dash at all (looking like the box was missing a side, when
    // it was really just an unlucky dash-phase alignment). Distinct from
    // the free-hand selection by color (yellow vs cyan) instead. Shared
    // by both Pick for me and Fixed-size drag — whichever is currently
    // active owns toolRect, so this doesn't need to know which one it is.
    if (activeTool && toolRect) {
      ctx.save();
      ctx.strokeStyle = "#ffe66d";
      const boxLineWidth = 2;
      ctx.lineWidth = boxLineWidth;
      ctx.setLineDash([]);
      // Inset by half the line width — strokeRect draws centered on the
      // path, so a box sitting exactly at the canvas edge (x=0, or the
      // right/bottom edge) would otherwise have half its stroke drawn
      // off-canvas and invisible, making that side look missing/thinner.
      ctx.strokeRect(
        toolRect.x + boxLineWidth / 2,
        toolRect.y + boxLineWidth / 2,
        assistSize - boxLineWidth,
        assistSize - boxLineWidth
      );
      ctx.restore();

      drawArrowAtRect({ x: toolRect.x, y: toolRect.y, w: assistSize, h: assistSize }, "#ffe66d");
    }

    // Highlight the pending-confirmation selection — shown once "Use
    // this selection" (or a completed free-hand drag) has actually been
    // confirmed and a real quote is back, reinforcing where the priced
    // selection is on top of whichever assist box led to it. Arrowed the
    // same way as the assist box above it replaces visually.
    if (pendingQuote) {
      ctx.save();
      ctx.strokeStyle = "#8aff8a";
      ctx.lineWidth = 3;
      ctx.setLineDash([]);
      ctx.strokeRect(pendingQuote.rect.x + 1.5, pendingQuote.rect.y + 1.5, pendingQuote.rect.w - 3, pendingQuote.rect.h - 3);
      ctx.restore();

      drawArrowAtRect(pendingQuote.rect, "#8aff8a");
    }
  }, [drag, myRegions, config, tick, activeTool, toolRect, assistSize, pendingQuote]);

  if (pageMode === "loading") {
    return (
      <div style={{ color: "var(--text-primary)", minHeight: "100vh" }}>
        {/* ONE SENTENCE, ONE SPELLING (1.555.0). This copy used ASCII
            punctuation and the banner below used typographic, and a buyer
            can be shown either - whichever they land in. The widget had
            to match one of them to carry the same message, and two
            spellings of one sentence is not something to copy. */}
        <div style={{ padding: 20 }}>{error || (pendingPurchaseStatus === "checking" ? PENDING_PAYMENT_NOTICE : "Loading...")}</div>
      </div>
    );
  }

  if (pageMode === "maintenance") {
    return (
      <div style={{ color: "var(--text-primary)", minHeight: "100vh" }}>
        <div style={{ padding: "0 20px 20px", maxWidth: 600, margin: "0 auto" }}>
          {/* The heading IS the message. Rendering the server's full
              sentence underneath repeated it word for word — the API
              needs a complete sentence in one string, a page does not. */}
          <h2>Site down for maintenance</h2>
          <p style={{ opacity: 0.7 }}>We&apos;ll be back shortly.</p>
        </div>
        <Footer />
      </div>
    );
  }

  if (pageMode === "public-site-disabled") {
    return (
      <div style={{ color: "var(--text-primary)", minHeight: "100vh" }}>
        <div style={{ padding: "0 20px 20px", maxWidth: 600, margin: "0 auto" }}>
          <h2>This site isn't showing competitions publicly right now</h2>
          <p style={{ opacity: 0.7 }}>Check back later, or reach out to the operator directly.</p>
        </div>
        <Footer />
      </div>
    );
  }

  if (pageMode === "none" && noneInfo) {
    const upcoming = noneInfo.upcoming || [];
    return (
      <div style={{ color: "var(--text-primary)", minHeight: "100vh" }}>
        <div style={{ padding: "0 20px 20px", maxWidth: 600, margin: "0 auto" }}>
        <h2>No competition is currently active</h2>
        {upcoming.length === 0 ? (
          <p style={{ opacity: 0.7 }}>Check back soon — nothing is queued up yet.</p>
        ) : (
          <>
            <p style={{ opacity: 0.7 }}>Here's what's coming up:</p>
            <ul>
              {upcoming.map((c) => (
                <li key={c.id}>
                  <strong>{c.title}</strong> —{" "}
                  {c.startAt
                    ? `starts ${siteDateTime(c.startAt)}`
                    : `starts ${(c.startDelayMs / 60000).toFixed(0)}min after the current competition is answered`}
                </li>
              ))}
            </ul>
          </>
        )}
        {noneInfo.captureEmail && (
          <ReminderForm competitionId={upcoming[0]?.id} token={userToken} subscribed={noneInfo.reminderSubscribed} />
        )}
        </div>
        <Footer />
      </div>
    );
  }

  if (pageMode === "ended" && endedInfo) {
    const nextStartAt = endedInfo.next?.startAt;
    return (
      <div style={{ color: "var(--text-primary)", minHeight: "100vh" }}>
        <div style={{ padding: "0 20px 20px", maxWidth: 900, margin: "0 auto" }}>
        <h2>{endedInfo.title} — {endedInfo.wonByLottery ? "Lottery Drawn!" : "Solved!"}</h2>
        {endedInfo.simulationRunning && demoRouteToken && (
          <div>
            <SimulationBanner
              targetTime={nextStartAt}
              resolvedCompetitions={endedInfo.simulationResolvedCompetitions}
              totalCompetitions={endedInfo.simulationTotalCompetitions}
            />
          </div>
        )}
        <p style={{ opacity: 0.8, maxWidth: 700 }}>
          {endedInfo.wonByLottery ? (
            <>
              Nobody guessed it in time, so a winning ticket was drawn at random from purchased pixels
              {endedInfo.winningTicket != null && (
                <>
                  {" "}
                  (ticket #{endedInfo.winningTicket})
                </>
              )}
              . The answer was <strong>{endedInfo.answer}</strong>.{" "}
              {endedInfo.answeredByUsername ? (
                <>
                  Winning ticket held by <strong>{endedInfo.answeredByUsername}</strong>.
                </>
              ) : (
                "No eligible ticket was found (no logged-in purchases), so there's no winner to declare this time."
              )}
            </>
          ) : (
            <>
              The answer was <strong>{endedInfo.answer}</strong>, correctly guessed by{" "}
              <strong>{endedInfo.answeredByUsername}</strong>.
            </>
          )}
          {endedInfo.prizeMoney > 0 && endedInfo.answeredByUsername && (
            <>
              {" "}
              They win <strong>{symbol}{endedInfo.prizeMoney.toFixed(2)}</strong>!
            </>
          )}
        </p>
        {/* End of a simulated run, on the demo-route view only: something
            resolved, nothing is active, nothing is queued. Shown instead of
            the usual "next competition" line, which would otherwise leave a
            viewer waiting for something that isn't coming. Gated on
            demoRouteToken as well as the backend's own mock-only
            simulationEnded flag — belt and braces, so this wording can never
            reach the real public homepage between two genuine competitions.
            Nothing is deleted at this point: the result stays on screen, and
            the message clears by itself if a new batch is generated. */}
        {endedInfo.simulationEnded && demoRouteToken && (
          <p style={{ opacity: 0.8, maxWidth: 700 }}>
            <strong>Simulation ended, hope you enjoyed the show</strong>
          </p>
        )}
        {endedInfo.showCountdown && !(endedInfo.simulationEnded && demoRouteToken) && (
          <p style={{ opacity: 0.8, maxWidth: 700 }}>
            {!nextStartAt ? (
              "No confirmed date for the next competition yet — check back soon."
                          ) : (
              <>
                Next competition{endedInfo.next.title ? ` ("${endedInfo.next.title}")` : ""} starts in{" "}
                <strong>{formatLongCountdown(nextStartAt - serverNow())}</strong>
              </>
            )}
          </p>
        )}
        {endedInfo.captureEmail && (
          <ReminderForm competitionId={endedInfo.next?.id} token={userToken} subscribed={endedInfo.reminderSubscribed} />
        )}
        {endedImageUrl && (
          <img
            src={endedImageUrl}
            alt={`Revealed: ${endedInfo.title}`}
            style={{ width: "100%", maxWidth: endedInfo.imageWidth, display: "block", borderRadius: 8 }}
          />
        )}
        </div>
        <Footer />
      </div>
    );
  }

  const toImageCoords = (clientX, clientY) => {
    const rect = containerRef.current.getBoundingClientRect();
    const scaleX = config.imageWidth / rect.width;
    const scaleY = config.imageHeight / rect.height;
    return {
      x: (clientX - rect.left) * scaleX,
      y: (clientY - rect.top) * scaleY,
    };
  };

  const clampToolPos = (x, y) => ({
    x: Math.max(0, Math.min(config.imageWidth - assistSize, x)),
    y: Math.max(0, Math.min(config.imageHeight - assistSize, y)),
  });

  // Shared with the arrow actually drawn in the canvas effect below, so
  // the clickable/draggable area always matches what's visually on
  // screen exactly.
  const ARROW_GAP = 12;
  const ARROW_HEAD_LEN = 12;
  const ARROW_HEAD_WIDTH = 9;
  const ARROW_SHAFT_LEN = 26;

  // Bounding box (in image coordinates) around wherever the arrow is
  // currently drawn for a given rect — padded a bit for easier clicking,
  // especially on touch. Used so the arrow itself is a grabbable handle
  // for repositioning the fixed-size box, not just the box outline.
  const getArrowBounds = (rect) => {
    const centerX = rect.x + rect.w / 2;
    const spaceAbove = rect.y;
    const spaceBelow = config.imageHeight - (rect.y + rect.h);
    const pointFromAbove = spaceAbove >= spaceBelow;
    const tipY = pointFromAbove ? rect.y - ARROW_GAP : rect.y + rect.h + ARROW_GAP;
    const baseY = pointFromAbove ? tipY - ARROW_HEAD_LEN : tipY + ARROW_HEAD_LEN;
    const tailY = pointFromAbove ? baseY - ARROW_SHAFT_LEN : baseY + ARROW_SHAFT_LEN;
    const padding = 8;
    return {
      left: centerX - ARROW_HEAD_WIDTH - padding,
      right: centerX + ARROW_HEAD_WIDTH + padding,
      top: Math.min(tipY, tailY) - padding,
      bottom: Math.max(tipY, tailY) + padding,
    };
  };

  // True if (x, y) lands on either the active tool's box itself or its
  // arrow.
  const isOnToolHandle = (x, y) => {
    if (x >= toolRect.x && x <= toolRect.x + assistSize && y >= toolRect.y && y <= toolRect.y + assistSize) {
      return true;
    }
    const bounds = getArrowBounds({ x: toolRect.x, y: toolRect.y, w: assistSize, h: assistSize });
    return x >= bounds.left && x <= bounds.right && y >= bounds.top && y <= bounds.bottom;
  };

  const onMouseDown = (e) => {
    if (pendingQuote || pendingFreeEntry) return; // one selection/modal at a time
    const { x, y } = toImageCoords(e.clientX, e.clientY);
    if (freeEntryMode) {
      const px = Math.max(0, Math.min(config.imageWidth - 1, Math.floor(x)));
      const py = Math.max(0, Math.min(config.imageHeight - 1, Math.floor(y)));
      setPendingFreeEntry({ x: px, y: py });
      return;
    }
    if (activeTool) {
      if (!toolRect) return;
      // Only start repositioning if the click actually landed on the box
      // or its arrow — clicking elsewhere while a tool is active does
      // nothing, rather than falling back to free-hand drawing.
      if (!isOnToolHandle(x, y)) return;
      setToolDragOffset({ dx: x - toolRect.x, dy: y - toolRect.y });
      return;
    }
    setDrag({ startX: x, startY: y, x, y, w: 0, h: 0 });
  };

  const onMouseMove = (e) => {
    const { x, y } = toImageCoords(e.clientX, e.clientY);
    if (activeTool) {
      if (!toolDragOffset) return;
      setToolRect(clampToolPos(x - toolDragOffset.dx, y - toolDragOffset.dy));
      return;
    }
    if (!drag) return;
    const newDrag = {
      startX: drag.startX,
      startY: drag.startY,
      x: Math.min(drag.startX, x),
      y: Math.min(drag.startY, y),
      w: Math.abs(x - drag.startX),
      h: Math.abs(y - drag.startY),
    };
    setDrag(newDrag);
  };

  // Touch equivalents for mobile/tablet — finger-drag to select, same as
  // dragging with a mouse. `touch-action: none` on the container (below)
  // stops the browser from trying to scroll/zoom while dragging here,
  // which is what actually prevents the page fighting the selection
  // (relying on that CSS property is more reliable across browsers than
  // calling preventDefault() inside a touch handler, which some browsers
  // ignore for listeners they've decided to treat as passive).
  const onTouchStart = (e) => {
    if (pendingQuote || pendingFreeEntry) return;
    if (e.touches.length !== 1) return; // ignore pinch/multi-touch gestures
    const touch = e.touches[0];
    const { x, y } = toImageCoords(touch.clientX, touch.clientY);
    if (freeEntryMode) {
      const px = Math.max(0, Math.min(config.imageWidth - 1, Math.floor(x)));
      const py = Math.max(0, Math.min(config.imageHeight - 1, Math.floor(y)));
      setPendingFreeEntry({ x: px, y: py });
      return;
    }
    if (activeTool) {
      if (!toolRect) return;
      if (!isOnToolHandle(x, y)) return;
      setToolDragOffset({ dx: x - toolRect.x, dy: y - toolRect.y });
      return;
    }
    setDrag({ startX: x, startY: y, x, y, w: 0, h: 0 });
  };

  const onTouchMove = (e) => {
    if (e.touches.length !== 1) return;
    const touch = e.touches[0];
    const { x, y } = toImageCoords(touch.clientX, touch.clientY);
    if (activeTool) {
      if (!toolDragOffset) return;
      setToolRect(clampToolPos(x - toolDragOffset.dx, y - toolDragOffset.dy));
      return;
    }
    if (!drag) return;
    const newDrag = {
      startX: drag.startX,
      startY: drag.startY,
      x: Math.min(drag.startX, x),
      y: Math.min(drag.startY, y),
      w: Math.abs(x - drag.startX),
      h: Math.abs(y - drag.startY),
    };
    setDrag(newDrag);
  };

  const requestQuoteForRect = async (rect) => {
    if (!userToken) {
      // Don't even fetch a real quote — purchasing requires an account now,
      // so there's nothing useful a price would tell an anonymous visitor
      // that they could actually act on. Reuses the same dialog UI as a
      // real quote, just showing a login prompt instead of price/confirm.
      setPendingQuote({ rect, needsLogin: true });
      return;
    }
    try {
      const ownerTokens = myRegions.map((r) => r.ownerToken);
      const data = await wsRequest("quote.get", { ...rect, ownerTokens }, userToken);
      setPendingQuote({
        rect,
        price: data.price,
        available: data.available,
        newPixels: data.newPixels,
        alreadyOwnedPixels: data.alreadyOwnedPixels,
        creditsGained: data.creditsGained,
      });
    } catch (err) {
      showToast(err.message || "Could not reach backend for a quote.", "error");
    }
  };

  // Pick for me and Fixed-size drag share one toggle: activating either
  // shows a box the user can see and drag before confirming, rather than
  // committing to a quote immediately. See findAssistPosition and the
  // effect above for how each tool's initial position is chosen.
  const toggleTool = (tool) => {
    if (activeTool === tool) {
      setActiveTool(null);
      return;
    }
    setPendingQuote(null); // any pending quote from a previous selection method is no longer relevant
    setActiveTool(tool);
  };

  const confirmToolSelection = () => {
    if (!toolRect) return;
    requestQuoteForRect({ x: toolRect.x, y: toolRect.y, w: assistSize, h: assistSize });
  };

  const onMouseUp = async () => {
    if (activeTool) {
      setToolDragOffset(null); // stop repositioning until the box is grabbed again
      return;
    }
    if (!drag) return;
    const rect = { x: Math.round(drag.x), y: Math.round(drag.y), w: Math.round(drag.w), h: Math.round(drag.h) };
    setDrag(null);
    if (rect.w < config.minRegion || rect.h < config.minRegion) {
      // A true zero-size drag (a plain click, no movement at all) stays
      // silent — that's not an attempted selection. But if there was some
      // real drag distance and it just ended up under the minimum, the
      // person saw the price label the whole time and deserves to know
      // why nothing happened next, rather than the selection just
      // silently vanishing.
      if (rect.w > 0 || rect.h > 0) {
        showToast(`Selection too small — minimum is ${config.minRegion}×${config.minRegion}px.`, "error");
      }
      return;
    }
    requestQuoteForRect(rect);
  };

  const confirmPurchase = async (tokenOverride) => {
    setBusy(true);
    try {
      const ownerTokens = myRegions.map((r) => r.ownerToken);
      if (config.paymentProviderActive) {
        // Real payment provider active — hands off to its own hosted
        // checkout page rather than granting anything immediately here.
        // The buyer's browser is redirected away; confirmation, and the
        // actual grant, happen later, asynchronously, once the webhook
        // independently confirms payment (see the polling effect above
        // for what happens when they land back).
        const res = await fetch(`${API_BASE}/api/v1/purchase/start`, {
          method: "POST",
          headers: { "Content-Type": "application/json", "x-auth-token": tokenOverride || userToken },
          // The competition this quote was taken against. The server
          // refuses if it is no longer current — otherwise a buyer who
          // takes a while to decide can be sold pixels in a competition
          // that started after they chose their rectangle.
          body: JSON.stringify({ ...pendingQuote.rect, ownerTokens, competitionId: config?.competitionId, demoRouteToken: demoRouteToken || undefined }),
        });
        const data = await res.json();
        if (!res.ok) {
          if (res.status === 401) triggerSessionInvalid();
          throw new Error(data.error || "Could not start checkout.");
        }
        window.location.href = data.redirectUrl;
        return; // leaving the page — nothing further to do here
      }
      // --- mock payment step would go here (e.g. Stripe Checkout) ---
      const data = await wsRequest("purchase.create", { ...pendingQuote.rect, ownerTokens }, tokenOverride || userToken);
      setMyRegions((prev) => [
        ...prev,
        {
          ownerToken: data.ownerToken,
          competitionId: config.competitionId,
          ...pendingQuote.rect,
          tile: data.tile,
          privateUntil: data.privateUntil,
          isPublic: false,
        },
      ]);
      setPendingQuote(null);
      refreshPublicImage(config.competitionId);
      // Back to the default tool state — otherwise the assist box would
      // just sit on the spot that was literally just purchased, with the
      // Cancel/Use-this-selection buttons still showing until manually
      // dismissed.
      if (activeTool) setActiveTool(null);
    } catch (err) {
      showToast(err.message || "Purchase failed — could not reach backend.", "error");
    }
    setBusy(false);
  };

  const confirmFreeEntry = async () => {
    setBusy(true);
    try {
      const ownerTokens = myRegions.map((r) => r.ownerToken);
      const data = await wsRequest(
        "freeEntry.create",
        { x: pendingFreeEntry.x, y: pendingFreeEntry.y, ownerTokens },
        userToken
      );
      setMyRegions((prev) => [
        ...prev,
        {
          ownerToken: data.ownerToken,
          competitionId: config.competitionId,
          x: pendingFreeEntry.x,
          y: pendingFreeEntry.y,
          w: 1,
          h: 1,
          tile: data.tile,
          privateUntil: data.privateUntil,
          isPublic: false,
        },
      ]);
      setPendingFreeEntry(null);
      setFreeEntryMode(false);
      refreshPublicImage(config.competitionId);
      loadConfig(); // picks up freeEntryPixelUsed: true so the "use free entry" option disappears
    } catch (err) {
      showToast(err.message || "Free entry failed — could not reach backend.", "error");
    }
    setBusy(false);
  };

  return (
    <div style={{ color: "var(--text-primary)", padding: 20, paddingBottom: 20 + guessFormHeight }}>
      <div style={{ maxWidth: contentWidth, margin: "0 auto" }}>
        <h2>{config.title}</h2>
        {pendingPurchaseStatus === "checking" && (
          <div style={{ padding: 12, borderRadius: 8, border: "1px solid var(--border-color)", marginBottom: 14, fontSize: 14 }}>
            {PENDING_PAYMENT_NOTICE}
          </div>
        )}
        {config.simulationRunning && demoRouteToken && (
          <div>
            <SimulationBanner
              targetTime={config.finishesAt}
              resolvedCompetitions={config.simulationResolvedCompetitions}
              totalCompetitions={config.simulationTotalCompetitions}
              isCountingToOwnEnd
            />
          </div>
        )}
        <ConnectionStatusBanner status={connectionStatus} visible={connectionVisible} />

        {/* freeEntryPixelAvailable, not freeEntryEnabled — a partner can
            offer the free GUESS without the free pixel, and a button that
            exists and then gets refused is worse than no button. */}
        {(config.freeEntryPixelAvailable || config.freeEntryGuessAvailable) && (
          <div
            style={{
              border: "1px solid var(--accent-blue)",
              borderRadius: 8,
              padding: 12,
              marginBottom: 16,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 12,
              flexWrap: "wrap",
            }}
          >
            {freeEntryMode && !pendingFreeEntry ? (
              <>
                <span style={{ fontSize: 13 }}>
                  {/* BOTH FREE ROUTES, when both are on and neither is used.
                      A partner may offer the pixel and not the guess (see
                      freeEntryGuess in storage.js), and the guess may already
                      be spent — so this asks the same two flags the banner
                      above does rather than assuming. Requested 6 Sep. */}
                  🎁 Click anywhere on the image below to use your free entry on that pixel
                  {freeGuessStillToCome(config)
                    ? ", then enter your free guess in the form below."
                    : "."}
                </span>
                <button onClick={() => setFreeEntryMode(false)}>Cancel</button>
              </>
            ) : pendingFreeEntry ? (
              <span style={{ fontSize: 13 }}>🎁 Confirm your free entry pixel below.</span>
            ) : !userToken ? (
              <span style={{ fontSize: 13 }}>
                {/* SAYS WHAT IS ACTUALLY FREE, rather than assuming both
                    routes are on. A partner can offer one without the
                    other, and promising a free pixel that does not exist
                    is worse than not mentioning it. */}
                🎁 Free entry available — no purchase necessary.{" "}
                <NavLink to="/profile" style={{ color: "var(--accent-blue)" }}>
                  Log in
                </NavLink>{" "}
                to {freeRoutesSentence(config)}.
              </span>
            ) : config.freeEntryPixelUsed ||
              (config.freeEntryGuessAvailable && config.freeEntryGuessUsed) ? (
              <>
                <span style={{ fontSize: 13, opacity: 0.7 }}>
                  {/* WHAT THIS BUYER HAS TAKEN, ROUTE BY ROUTE.
                      This branch used to be reached only when the free
                      PIXEL had been used — so on a site offering the free
                      guess and no pixel, somebody who had claimed AND
                      spent their free entry still saw "Free entry
                      available". The used-state was unreachable.

                      Each route reports itself now, and says nothing about
                      one that is switched off. */}
                  {config.freeEntryPixelAvailable && config.freeEntryPixelUsed
                    ? "✓ You've used your free pixel for this competition."
                    : config.freeEntryPixelAvailable
                      ? "You still have a free pixel for this competition."
                      : ""}
                  {/* THREE STATES FOR THE GUESS, not two. Claiming and
                      spending are separate acts since 17 Sep 2026. */}
                  {!config.freeEntryGuessAvailable
                    ? ""
                    : !config.freeEntryGuessUsed
                      ? " You still have a free guess for this competition."
                      : (config.freeEntryCreditsRemaining || 0) > 0
                        ? /* SAYS WHAT IT IS FOR, NOT WHERE IT IS (1.927.0).
                             See the widget's copy of this banner for the
                             reasoning - the same wording, reported from a
                             partner page on 23 Oct 2026. */
                          " Your free entry credit is ready - use it on your next guess."
                        : " Your free entry credits have been used."}
                </span>
                {/* THE PIXEL STAYS REACHABLE AFTER CANCEL. Claiming the
                    guess credit sends the banner here, and this branch
                    used to be words only — "You still have a free pixel"
                    with nothing to press. Reported 21 Sep 2026. */}
                {canStillUseFreePixel(config) && (
                  <button
                    onClick={() => setFreeEntryMode(true)}
                    disabled={busy || !!pendingQuote || !!activeTool}>
                    Use free pixel
                  </button>
                )}
                {/* AND THE GUESS, when the pixel was taken without it —
                    a free pixel used before the guess route was switched
                    on, or through a page that could not take the guess
                    (the partner widget until 1.413.0). "You still have a
                    free guess" with nothing to press is the same fault as
                    the pixel above. */}
                {config.freeEntryGuessAvailable && !config.freeEntryGuessUsed && (
                  <button
                    onClick={async () => {
                      try {
                        await wsRequest("freeEntry.claimCredits", {}, userToken);
                      } catch (err) {
                        if (err?.status !== 409) {
                          showToast(err?.message || "Could not take the free entry.", "error");
                          return;
                        }
                      }
                      loadConfig();
                    }}
                    disabled={busy || !!pendingQuote || !!activeTool}>
                    Take free guess
                  </button>
                )}
              </>
            ) : (
              <>
                <span style={{ fontSize: 13 }}>
                  🎁 Free entry available — no purchase necessary. You can{" "}
                  {freeRoutesSentence(config)}.
                </span>
                {/* THE BUTTON NOW COVERS BOTH ROUTES.
                    It used to be pixel-only, and the comment here said
                    "the guess form below already offers it" — true while
                    the first guess of each competition was silently free.
                    It is not any more: since 17 Sep 2026 the free entry
                    is CLAIMED, which grants one entry's worth of credit
                    bound to this competition.

                    So one click does whatever this site offers: takes
                    the credits if the guess route is on and untaken, and
                    enters "click a pixel" mode if the pixel route is on.
                    With only the guess offered there is nothing to click
                    afterwards, and that is fine — the credit is in the
                    account and the normal guess form spends it. */}
                {(config.freeEntryPixelAvailable ||
                  (config.freeEntryGuessAvailable && !config.freeEntryGuessUsed)) && (
                  <button
                    onClick={async () => {
                      if (config.freeEntryGuessAvailable && !config.freeEntryGuessUsed) {
                        try {
                          await wsRequest("freeEntry.claimCredits", {}, userToken);
                          // Picks up freeEntryGuessUsed: true, so the banner
                          // stops offering what has just been taken — the
                          // same reason the pixel claim calls it.
                          loadConfig();
                        } catch (err) {
                          // ALREADY CLAIMED IS NOT AN ERROR WORTH STOPPING
                          // FOR — the pixel half may still be available,
                          // and refusing the whole click would strand
                          // somebody whose credits arrived a moment ago.
                          if (err?.status !== 409) {
                            showToast(err?.message || "Could not take the free entry.", "error");
                            return;
                          }
                        }
                      }
                      if (config.freeEntryPixelAvailable) setFreeEntryMode(true);
                    }}
                    disabled={busy || !!pendingQuote || !!activeTool}>
                    Use free entry
                  </button>
                )}
              </>
            )}
          </div>
        )}

        <div style={{ display: "flex", gap: 16, flexWrap: "wrap", alignItems: "stretch", marginBottom: 16, width: "100%" }}>
          <div style={{ flex: "1 1 300px", border: "1px solid var(--border-color)", borderRadius: 8, padding: 12, boxSizing: "border-box" }}>
            <EndsInCountdown endsAt={config.endsAt} />
            <p style={{ opacity: 0.8, margin: 0 }}>
              Drag on the image to select a rectangle. You'll be quoted a price (
              {symbol}{(config.pricePerPixel || 0).toFixed(2)}/pixel), and after "payment" your selection is cut out of the
              real hidden image and shown to you privately for {Math.round(config.privateViewMs / 60000)}{" "}
              minute{Math.round(config.privateViewMs / 60000) === 1 ? "" : "s"} before it becomes public for everyone.
              {config.prizeMoney > 0 && (
                <>
                  {" "}
                  Correctly guess what the hidden image is for a chance to win{" "}
                  <strong>{symbol}{config.prizeMoney.toFixed(2)}</strong>!
                </>
              )}{" "}
              <span style={{ opacity: 0.6 }}>
                ({config.answerMode === "exact" ? "Exact match required" : "Fuzzy match accepted"})
              </span>
            </p>
          </div>

          <div style={{ flex: "1 1 300px", border: "1px solid var(--border-color)", borderRadius: 8, padding: 12, boxSizing: "border-box" }}>
            <div style={{ fontSize: 13, opacity: 0.7, marginBottom: 8 }}>
              Not sure exactly where to select? Let us pick for you or drag a pre-sized box around instead of drawing
              one freehand.
            </div>
            {!userToken ? (
              <div style={{ fontSize: 13, opacity: 0.6 }}>
                <NavLink to="/profile/preferences" style={{ color: "var(--accent-blue)" }}>
                  Log in and set a preferred spend amount
                </NavLink>{" "}
                to enable these.
              </div>
            ) : !preferredSpend ? (
              <div style={{ fontSize: 13, opacity: 0.6 }}>
                Set a preferred spend amount under{" "}
                <NavLink to="/profile/preferences" style={{ color: "var(--accent-blue)" }}>
                  My account → Competition Preferences
                </NavLink>{" "}
                to enable these.
              </div>
            ) : (
              <div>
                <div style={{ fontSize: 13, opacity: 0.7, marginBottom: 8 }}>
                  ~{assistSize}×{assistSize}px selections (based on your {symbol}
                  {preferredSpend} preference)
                </div>
                <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                  <button
                    onClick={() => toggleTool("pick")}
                    disabled={freeEntryMode}
                    style={activeTool === "pick" ? { borderColor: "var(--accent-yellow)", color: "var(--accent-yellow)" } : undefined}
                  >
                    🎲 {activeTool === "pick" ? "Cancel Pick for me" : "Pick for me"}
                  </button>
                  <button
                    onClick={() => toggleTool("fixed")}
                    disabled={freeEntryMode}
                    style={activeTool === "fixed" ? { borderColor: "var(--accent-yellow)", color: "var(--accent-yellow)" } : undefined}
                  >
                    📐 {activeTool === "fixed" ? "Cancel fixed-size drag" : "Fixed-size drag"}
                  </button>
                  {activeTool && (
                    <button onClick={confirmToolSelection} disabled={busy || !toolRect}>
                      ✓ Use this selection
                    </button>
                  )}
                  {activeTool && !toolRect && (
                    <span style={{ fontSize: 12, opacity: 0.6, alignSelf: "center" }}>Finding an open spot…</span>
                  )}
                </div>
              </div>
            )}
          </div>

          <div style={{ flex: "1 1 300px", border: "1px solid var(--border-color)", borderRadius: 8, padding: 12, boxSizing: "border-box" }}>
            <div style={{ fontSize: 13, opacity: 0.7, marginBottom: 8 }}>Competition status</div>
            <div style={{ fontSize: 13, display: "grid", gridTemplateColumns: "auto 1fr", rowGap: 6, columnGap: 10 }}>
              <span style={{ opacity: 0.6 }}>Status:</span>
              <span style={isLottery ? { color: "var(--accent-yellow)" } : undefined}>
                {isLottery ? "Lottery draw pending" : "In Play"}
              </span>
              <span style={{ opacity: 0.6 }}>Type:</span>
              <span>{isLottery ? "Lottery" : "Competition"}</span>
              <span style={{ opacity: 0.6 }}>Private view length:</span>
              <span>
                {Math.round(config.privateViewMs / 60000)} minute{Math.round(config.privateViewMs / 60000) === 1 ? "" : "s"}
              </span>
              {!isLottery && config.lotteryConvertAt && (
                <>
                  <span style={{ opacity: 0.6 }}>Converts to lottery in:</span>
                  <span>{formatLongCountdown(config.lotteryConvertAt - serverNow())}</span>
                </>
              )}
              {isLottery && config.finishesAt && (
                <>
                  <span style={{ opacity: 0.6 }}>Draw happens in:</span>
                  <span style={{ color: "var(--accent-yellow)" }}>{formatLongCountdown(config.finishesAt - serverNow())}</span>
                </>
              )}
              <span style={{ opacity: 0.6 }}>Started:</span>
              <span>{config.startedAt ? siteDateTime(config.startedAt) : "—"}</span>
              <span style={{ opacity: 0.6 }}>Finishes:</span>
              <span>{config.finishesAt ? siteDateTime(config.finishesAt) : "—"}</span>
            </div>
            {isLottery && (
              <div style={{ fontSize: 12, opacity: 0.7, marginTop: 8, color: "var(--accent-yellow)" }}>
                Entries are frozen — this competition has moved to a lottery draw. A random ticket will be picked at
                the finish time above. You can still buy pixels for more tickets and better odds.
              </div>
            )}
          </div>
        </div>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16, flexWrap: "wrap" }}>
        <div
          ref={containerRef}
          style={{
            position: "relative",
            flex: "1 1 320px", // can shrink to make room for the side panels; hits a real floor below, which is what lets flex-wrap trigger and push the panels below instead of continuing to overflow
            minWidth: 320, // the actual floor — minWidth:0 was the mistake: it let the browser keep shrinking indefinitely instead of ever triggering the wrap fallback, since flex-wrap only kicks in once an item truly can't shrink any further
            maxWidth: config.imageWidth, // never upscaled beyond its real native size just because there's room
            aspectRatio: `${config.imageWidth} / ${config.imageHeight}`,
            cursor: "crosshair",
            userSelect: "none",
            touchAction: "none", // stop the browser scrolling/zooming while finger-dragging a selection
          }}
          onMouseDown={onMouseDown}
          onMouseMove={onMouseMove}
          onMouseUp={onMouseUp}
          onMouseLeave={() => { setDrag(null); setToolDragOffset(null); }}
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onMouseUp}
          onTouchCancel={() => { setDrag(null); setToolDragOffset(null); }}
        >
          {publicImageUrl && (
            <img
              src={publicImageUrl}
              alt="Public reveal so far"
              draggable={false}
              style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", objectFit: "cover", display: "block" }}
            />
          )}
          <canvas
            ref={canvasRef}
            width={config.imageWidth}
            height={config.imageHeight}
            style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%" }}
          />
          {drag && drag.w > 0 && drag.h > 0 && (
            <div
              style={{
                position: "absolute",
                left: `${(drag.x / config.imageWidth) * 100}%`,
                top: `${(drag.y / config.imageHeight) * 100}%`,
                transform: "translateY(-100%) translateY(var(--drag-label-offset))",
                background: "var(--bg-panel-alt)",
                border: "1px solid var(--border-color-light)",
                borderRadius: 4,
                padding: "var(--drag-label-padding)",
                fontSize: "var(--drag-label-font-size)",
                color: drag.w < config.minRegion || drag.h < config.minRegion ? "var(--accent-red)" : "var(--text-primary)",
                pointerEvents: "none",
                whiteSpace: "nowrap",
              }}
            >
              {drag.w < config.minRegion || drag.h < config.minRegion
                ? `Too small (min ${config.minRegion}×${config.minRegion}px)`
                : `~${symbol}${(drag.w * drag.h * config.pricePerPixel).toFixed(2)}`}
            </div>
          )}
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 16, width: "100%", maxWidth: "var(--sidebar-max-width)" }}>
          <StatsPanel token={userToken} competitionId={config.competitionId} />
          <LastEntriesPanel competitionId={config.competitionId} />
        </div>
      </div>
      </div>

      <div
        ref={guessFormRef}
        style={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 50,
          background: "var(--bg-page)",
          borderTop: "1px solid var(--border-color)",
          padding: "12px 20px",
          boxSizing: "border-box",
        }}
      >
        <div style={{ maxWidth: contentWidth, margin: "0 auto" }}>
          <GuessForm
            token={userToken}
            answerMode={config.answerMode}
            creditsPerEntry={config.creditsPerEntry}
            creditsPerMajorUnitOnPixels={config.creditsPerMajorUnitOnPixels}
            isLottery={isLottery}
            onCorrect={loadConfig} onEntryUsed={loadConfig}
            connectionStale={connectionStale}
          />
        </div>

        {pendingQuote && pendingQuote.needsLogin ? (
          <div
            style={{
              position: "fixed",
              bottom: guessFormHeight + 20,
              left: 20,
              zIndex: 60,
              background: "var(--bg-panel-alt)",
              padding: 24,
              borderRadius: 10,
              minWidth: 280,
              maxWidth: 320,
              boxShadow: "0 4px 20px rgba(0,0,0,0.5)",
              border: "1px solid var(--border-color)",
            }}
          >
            <h3 style={{ marginTop: 0 }}>You need to be logged in to participate</h3>
            <AuthForm onSuccess={(u, freshToken) => confirmPurchase(freshToken)} />
            <button onClick={() => setPendingQuote(null)} style={{ marginTop: 10 }}>
              Cancel
            </button>
          </div>
        ) : (
          pendingQuote && (
            <div
              style={{
                position: "fixed",
                bottom: guessFormHeight + 20,
                left: 20,
                zIndex: 60,
                background: "var(--bg-panel-alt)",
                padding: 24,
                borderRadius: 10,
                minWidth: 280,
                maxWidth: 320,
                boxShadow: "0 4px 20px rgba(0,0,0,0.5)",
                border: "1px solid var(--border-color)",
              }}
            >
              <h3 style={{ marginTop: 0 }}>Confirm your selection</h3>
              <p>
                Area: {pendingQuote.rect.w}×{pendingQuote.rect.h}px
                <br />
                Price: <strong>{symbol}{(pendingQuote.price || 0).toFixed(2)}</strong> ({pendingQuote.newPixels} new px)
                {pendingQuote.alreadyOwnedPixels > 0 && (
                  <>
                    <br />
                    <span style={{ opacity: 0.7 }}>
                      {pendingQuote.alreadyOwnedPixels}px already paid for — not charged again
                    </span>
                  </>
                )}
                {pendingQuote.creditsGained > 0 && (
                  <>
                    <br />
                    <span style={{ color: "var(--accent-green)" }}>
                      You'll earn {pendingQuote.creditsGained} credit{pendingQuote.creditsGained === 1 ? "" : "s"} with this purchase!
                    </span>
                  </>
                )}
              </p>
              {pendingQuote.newPixels === 0 && (
                <p style={{ color: "var(--accent-red)" }}>This area is already fully revealed or already yours — nothing new to buy here.</p>
              )}
              <div style={{ display: "flex", gap: 10, marginTop: 16 }}>
                <button onClick={() => setPendingQuote(null)} disabled={busy}>
                  Cancel
                </button>
                <button onClick={() => confirmPurchase()} disabled={busy || connectionStale || !pendingQuote.available}>
                  {connectionStale ? "Reconnecting" : busy ? "Processing…" : `Pay ${symbol}${(pendingQuote.price || 0).toFixed(2)}`}
                </button>
              </div>
            </div>
          )
        )}

        {pendingFreeEntry && (
          <div
            style={{
              position: "fixed",
              bottom: guessFormHeight + 20,
              left: 20,
              zIndex: 60,
              background: "var(--bg-panel-alt)",
              padding: 24,
              borderRadius: 10,
              minWidth: 280,
              maxWidth: 320,
              boxShadow: "0 4px 20px rgba(0,0,0,0.5)",
              border: "1px solid var(--accent-blue)",
            }}
          >
            <h3 style={{ marginTop: 0 }}>Use your free entry?</h3>
            <p>
              This uses your one free pixel selection for this competition — free, no purchase necessary. You only
              get one, and this can't be undone.
            </p>
            <div style={{ display: "flex", gap: 10, marginTop: 16 }}>
              <button onClick={() => setPendingFreeEntry(null)} disabled={busy}>
                Cancel
              </button>
              <button onClick={confirmFreeEntry} disabled={busy}>
                {connectionStale ? "Reconnecting" : busy ? "Processing…" : "Use free entry"}
              </button>
            </div>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
